// Cart Page - Shopping cart grouped by branch
import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  ArrowLeft,
  ShoppingCart,
  MapPin,
  Trash2,
  Package,
  Truck,
  TrendingDown,
  TrendingUp,
  UserRound,
  Hourglass,
} from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import {
  Button,
  CartItemStockRow,
  QuantitySelector,
  useToast,
  VanStockToggle,
} from '../../components/radix'
import { getCartItemStockView } from '../../utils/cartItemStock'
import {
  ManageOrderSheet,
  type ManageOrderApplyResult,
} from '../../components/engineer/cart/ManageOrderSheet'
import {
  ChangeItemSheet,
  type ChangeItemApplyResult,
} from '../../components/engineer/cart/ChangeItemSheet'
import { useCart } from '../../auth/hooks/useCart'
import {
  updateCartItemDeliverySource,
  type CartItemResponse,
  type CartResponse,
} from '../../auth/services/cart-api'
import { useMe } from '../../auth/hooks/useMe'
import { useLocationChange } from '../../auth/hooks/useLocationChange'
import { usePostcodeSync } from '../../hooks/usePostcodeSync'
import { useMergeSameDeliveryLines } from '../../hooks/useMergeSameDeliveryLines'
import { api } from '../../services/api'
import { formatEarliestDate } from '../../utils/datetime'
import { basketGroupDate } from '../../utils/fulfillmentDates'
import type { CartItem, FulfillmentOption } from '../../types'
import { perUnitPrice as perUnit, roundToPenny } from '../../utils/pricing'
import { toastClearanceRef } from '../../utils/heightVarRef'
import {
  readCartFulfillmentMap,
  readItemFulfillmentMap,
  removeItemFulfillmentMethod,
  writeCartFulfillmentMethod,
  writeItemFulfillmentMethod,
} from '../../utils/cartFulfillment'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

// Group cart items by supplier + branch + fulfillment method (BM-015) +
// backorder position (DEV-51018). A supplier+branch+method can now appear
// TWICE — once for in-stock items, once for backordered items — so an
// engineer isn't waiting on a 5–7 day SLA for a line they could collect
// today.
interface BranchGroup {
  supplierId: string | number
  branchId: string | number | null
  supplierName: string
  branchName: string
  distanceMiles: number | null
  fulfillmentMethod: 'pickup' | 'delivery'
  // Every item in this group is on backorder in its chosen channel.
  isBackorder: boolean
  items: CartItem[]
  total: number
}

// Per-unit price for a cart line — delegates to the shared pricing helper so
// the "divide by multiple" rule has a single definition across the storefront.
const perUnitPrice = (item: CartItem): number =>
  perUnit(item.price, item.quantityMultiple)

// DEV-51046: how far a collection branch has to be before the cart flags it
// after a move.
const FAR_BRANCH_MILES = 10

/**
 * Whether a branch card should show the far-branch warning in place of the
 * plain distance: a collection line was kept at this branch through a location
 * change and the branch is now a long drive away. Delivery is exempt — nobody
 * is driving there (AC2, AC3, AC4).
 */
function isFarCollectionBranch(
  keptSinceMove: boolean,
  method: 'pickup' | 'delivery',
  distanceMiles: number | null,
): boolean {
  return (
    keptSinceMove &&
    method === 'pickup' &&
    distanceMiles != null &&
    distanceMiles > FAR_BRANCH_MILES
  )
}

// DEV-51018: is this line on backorder in the chosen channel? Reads the
// flag stamped by cartWithStock — a line is on backorder when requested qty
// exceeds available branch+DC stock (or stock is zero) AND backorder is
// allowed. Grouper keys on this so an engineer's four-line basket can
// produce one in-stock order + one backorder order for the same branch.
function isItemBackorderInMethod(item: CartItem, method: 'pickup' | 'delivery'): boolean {
  return method === 'pickup'
    ? item.isPickupBackorder === true
    : item.isDeliveryBackorder === true
}

function groupCartByBranch(
  cartItems: CartItem[],
  getItemMethod: (item: CartItem) => 'pickup' | 'delivery',
  // DEV-51268: while a line is being edited, the cart pins it to its
  // pre-edit backorder position so the row doesn't jump between sections
  // mid-tap. The caller passes a resolver that returns the frozen flags for
  // editing lines and undefined for everyone else.
  getPinnedBackorderFlags?: (item: CartItem) => { isPickupBackorder: boolean; isDeliveryBackorder: boolean } | undefined,
): BranchGroup[] {
  const groups = new Map<string, BranchGroup>()

  // Method is baked into the key: same (supplier, branch) with two different
  // methods intentionally produces two groups so each renders as its own
  // card under the correct COLLECTION / DELIVERY section. Cross-branch
  // delivery redirection still applies at order placement in Checkout.
  //
  // Delivery-mode keying: when the adapter annotated a cross-branch
  // delivery source, key/label the group by that source so two lines
  // shipping from the same source branch collapse into one card even
  // when their own pickup branches differ. Matches Checkout's
  // groupOrdersByBranch — without this, the cart shows two identical-
  // looking "Delivered from CASTLEFORD" cards that Checkout would then
  // merge into a single order. Pickup mode is always keyed by own branch.
  cartItems.forEach((item) => {
    const method = getItemMethod(item)
    const useDeliverySource =
      method === 'delivery' && item.deliverySourceBranchId != null
    const groupBranchId = useDeliverySource
      ? item.deliverySourceBranchId
      : item.branchId
    const groupBranchName = useDeliverySource
      ? (item.deliverySourceBranchName ?? item.branchName)
      : item.branchName
    const groupDistance = useDeliverySource
      ? (item.deliverySourceBranchDistanceMiles ?? item.distanceMiles)
      : item.distanceMiles
    // DEV-51018: backorder position is part of the key so a
    // supplier+branch+method combination produces an in-stock card and a
    // backorder card as siblings, each placeable on its own date.
    // DEV-51268: if this line is currently being edited, use the frozen
    // flags so it stays in the group it was in when editing began.
    const pinnedFlags = getPinnedBackorderFlags?.(item)
    const backorder = pinnedFlags
      ? method === 'pickup'
        ? pinnedFlags.isPickupBackorder
        : pinnedFlags.isDeliveryBackorder
      : isItemBackorderInMethod(item, method)
    const key = `${item.supplierId}-${groupBranchId}-${method}-${backorder ? 'bo' : 'is'}`

    if (!groups.has(key)) {
      groups.set(key, {
        supplierId: item.supplierId,
        branchId: groupBranchId,
        supplierName: item.supplierName,
        branchName: groupBranchName,
        distanceMiles: groupDistance,
        fulfillmentMethod: method,
        isBackorder: backorder,
        items: [],
        total: 0,
      })
    }

    const group = groups.get(key)!
    group.items.push(item)
    group.total += roundToPenny(perUnitPrice(item) * item.quantity)
  })

  return Array.from(groups.values()).sort((a, b) => {
    // DEV-51018: for the same supplier+branch+method, the in-stock card
    // must land above its backorder sibling — the engineer sees the
    // today-collectable order first, then the SLA one.
    if (
      a.supplierId === b.supplierId &&
      String(a.branchId) === String(b.branchId) &&
      a.fulfillmentMethod === b.fulfillmentMethod &&
      a.isBackorder !== b.isBackorder
    ) {
      return a.isBackorder ? 1 : -1
    }
    // Sort by distance (closest first)
    if (a.distanceMiles === null) return 1
    if (b.distanceMiles === null) return -1
    return a.distanceMiles - b.distanceMiles
  })
}

export function CartPage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  // Cart query key (tenant-scoped to prevent cache bleed)
  const cartQueryKey = ['cart', tenantSlug || 'default']

  // Use backend cart instead of sessionStorage
  const {
    cart,
    isLoading,
    updateCartItem,
    updateCartLine,
    updateVanStockToggle,
    addToCartAsync,
    removeCartItemAsync,
    clearCart,
    isRemoving,
    isUpdating,
    mergeCartLines,
  } = useCart()
  const { data: meData } = useMe()
  const { lineBranchesAtMove } = useLocationChange()

  const [session, setSession] = useState<EngineerSession | null>(null)
  const [showProfileDialog, setShowProfileDialog] = useState(false)
  // BM-004 / BM-015: fulfillment is resolved per cart line via a two-tier map.
  //   1. `itemFulfillment` — line-level overrides written by Manage Order
  //      (BM-014) and Change Item (BM-015). Wins over the group fallback so
  //      a single (supplier, branch) can hold both a pickup line and a
  //      delivery line and render as two separate cards.
  //   2. `fulfillmentByGroup` — group-level fallback, seeded from the
  //      ProductDetail on-add bridge so a "delivery" pick made when adding
  //      the item is honoured on the first render.
  // Explicit user selections are stored; defaults ("pickup unless the item
  // can only deliver") are resolved lazily by getItemFulfillment.
  const [itemFulfillment, setItemFulfillment] = useState<Map<string, 'pickup' | 'delivery'>>(
    () => new Map(Object.entries(readItemFulfillmentMap())),
  )
  const [fulfillmentByGroup, setFulfillmentByGroup] = useState<Map<string, 'pickup' | 'delivery'>>(
    () => new Map(Object.entries(readCartFulfillmentMap())),
  )
  const [itemToRemove, setItemToRemove] = useState<{
    itemId: string
    productName: string
  } | null>(null)
  // BM-014: Manage Order sheet — currently open group (null = closed).
  const [manageOrderGroup, setManageOrderGroup] = useState<BranchGroup | null>(null)
  // DEV-51278: true when Manage Order was opened from a far branch's
  // "Change branch" link, so it opens on its branch list.
  const [manageOrderShowsBranches, setManageOrderShowsBranches] = useState(false)
  // BM-015: Change Item sheet — currently open line (null = closed).
  const [changeItem, setChangeItem] = useState<CartItem | null>(null)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [isClearingCart, setIsClearingCart] = useState(false)
  const [emptyQtyItemIds, setEmptyQtyItemIds] = useState<Set<string>>(new Set())
  const [pendingQuantities, setPendingQuantities] = useState<Map<string, number>>(new Map())

  // Debounce ref for quantity updates
  const debouncedUpdateRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const pendingQuantityClearRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  // DEV-51268: lines the engineer is currently editing. While a line is in
  // this set, groupCartByBranch reads the frozen backorder flags in
  // editingSnapshotsRef so the row stays in its original section instead of
  // jumping to Backorder mid-tap. The exit timers debounce (800ms) each
  // line's exit from editing state so a burst of +/+ / typing counts as
  // one edit; when the timer fires we re-evaluate and toast on move-in.
  // cartWithStockRef is read by the exit callback so the toast decision
  // sees the LATEST live flags — the async stock check often completes
  // after the last tap, which is exactly the transition we're detecting.
  // postEditWatchRef covers the leakier case: the exit timer fires before
  // the stock response lands, so exitEditing sees stale in-stock flags. We
  // keep the snapshot around for up to 5s and let a useEffect on
  // cartWithStock fire the toast when the flag actually flips.
  const [editingItemIds, setEditingItemIds] = useState<Set<string>>(new Set())
  const editingSnapshotsRef = useRef<Map<string, { isPickupBackorder: boolean; isDeliveryBackorder: boolean }>>(new Map())
  const exitEditingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const cartWithStockRef = useRef<CartItem[]>([])
  const postEditWatchRef = useRef<Map<string, { snapshot: { isPickupBackorder: boolean; isDeliveryBackorder: boolean }; timerId: ReturnType<typeof setTimeout> }>>(new Map())
  // Analytics: the quantity a line held before the engineer started changing
  // it. Captured here because the handler below writes the new quantity into
  // the query cache immediately for instant UI and only then debounces the
  // backend call — so by the time the mutation runs, every "before" value it
  // could read is already the new one. Set once per burst of taps and cleared
  // when the debounced call fires, so 2 -> 3 -> 4 -> 5 reports a change from
  // 2, not from 4.
  const quantityBeforeChangeRef = useRef<Map<string, number>>(new Map())
  const stockCheckDebounceRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const stockCheckSeqRef = useRef<Map<string, number>>(new Map())
  const cartRef = useRef(cart)
  const latestStockLimitsRef = useRef<Map<string, { branchStock: number; maxStock: number; hasDc: boolean; backorderFromApi: boolean }>>(new Map())
  const autoStockCheckKeysRef = useRef<Set<string>>(new Set())
  const [lineStockOverrides, setLineStockOverrides] = useState<Map<string, any>>(new Map())
  const [checkingStockItemIds, setCheckingStockItemIds] = useState<Set<string>>(new Set())
  // Keys of overrides that came from a Change Item apply seed (not from
  // scheduleLineStockCheck). Tracked separately so the sweep below only
  // touches OUR bridging seeds — scheduleLineStockCheck's overrides are
  // authoritative for their own line and must persist until the engineer
  // changes qty again.
  const changeItemSeedKeysRef = useRef<Set<string>>(new Set())
  // Self-heal: signatures of (itemId, live delivery source) already synced to
  // the DB this session, so the effect below only PATCHes when the value
  // actually changes and never spins.
  const deliverySourceSyncRef = useRef<Set<string>>(new Set())

  // Load session on mount
  useEffect(() => {
    const savedSession = sessionStorage.getItem('engineerSession')
    if (savedSession) {
      setSession(JSON.parse(savedSession))
    } else {
      navigate(`/${tenantSlug}/engineer`)
    }
  }, [navigate, tenantSlug])

  // Check if postcode changed on another device (cross-device sync)
  usePostcodeSync(session)

  useEffect(() => {
    cartRef.current = cart
  }, [cart])

  useEffect(() => {
    if (pendingQuantities.size === 0) return
    setPendingQuantities(prev => {
      let changed = false
      const next = new Map(prev)
      cart.forEach(item => {
        if (item.id && next.get(item.id) === item.quantity) {
          next.delete(item.id)
          const timer = pendingQuantityClearRef.current.get(item.id)
          if (timer) {
            clearTimeout(timer)
            pendingQuantityClearRef.current.delete(item.id)
          }
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [cart, pendingQuantities.size])

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateRef.current.forEach((timer) => clearTimeout(timer))
      debouncedUpdateRef.current.clear()
      pendingQuantityClearRef.current.forEach((timer) => clearTimeout(timer))
      pendingQuantityClearRef.current.clear()
      stockCheckDebounceRef.current.forEach((timer) => clearTimeout(timer))
      stockCheckDebounceRef.current.clear()
      exitEditingTimersRef.current.forEach((timer) => clearTimeout(timer))
      exitEditingTimersRef.current.clear()
      postEditWatchRef.current.forEach((watch) => clearTimeout(watch.timerId))
      postEditWatchRef.current.clear()
    }
  }, [])

  const stockLimitKey = (productId: string | number, branchId: string | number | null) =>
    `${String(productId)}::${String(branchId)}`

  // Prepare cart items for the page-load/full-cart stock fetch. Quantity is
  // deliberately excluded so ordinary cart qty edits do not reload every line.
  const cartStockItems = useMemo(() =>
    cart
      .filter(item => item.branchId !== null)
      .map(item => ({
        raw_product_id: item.productId,
        branch_id: String(item.branchId),
      })),
    [cart]
  )

  // Debounce the stock-fetch inputs so rapid qty changes don't fire a live
  // supplier call on every step — only re-fetch once the user settles (~600ms).
  const cartStockItemsKey = JSON.stringify(cartStockItems)
  const [debouncedStockKey, setDebouncedStockKey] = useState(cartStockItemsKey)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedStockKey(cartStockItemsKey), 600)
    return () => clearTimeout(timer)
  }, [cartStockItemsKey])
  const debouncedStockItems = useMemo(
    () => JSON.parse(debouncedStockKey) as typeof cartStockItems,
    [debouncedStockKey],
  )

  // Fetch live stock for all cart items (cart-specific endpoint - queries exact branches)
  const {
    data: liveStockData,
    isLoading: isLoadingStock,
    isFetching: isFetchingStock,
  } = useQuery({
    queryKey: ['cart-stock', debouncedStockKey, session?.postcode],
    queryFn: () => api.getCartStock(
      debouncedStockItems,
      session!.postcode,
      session!.latitude,
      session!.longitude
    ),
    enabled: cart.length > 0 && !!session?.postcode && debouncedStockItems.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on tab switch
    // Keep the previous query's data available while a new (debounced)
    // key is fetching. Without this, liveStockData goes undefined the
    // moment debouncedStockKey shifts — effectiveLiveStockData's early
    // return then drops the lineStockOverrides too, so the Change Item
    // apply seed vanishes during the ~600ms fetch window and the header
    // flickers back to the pickup branch. Placeholder keeps the previous
    // entries in place so overrides continuously merge on top until the
    // fresh response arrives.
    placeholderData: keepPreviousData,
  })

  // Sweep Change Item seeds once the debounced cart-stock refetch has
  // returned an authoritative entry for the same (product, branch) key.
  // Without this the seed shadows the fresh data forever (effectiveLiveStockData
  // merges overrides AFTER liveStockData, so overrides always win), leaving
  // the cart on the sheet snapshot for the whole session.
  useEffect(() => {
    if (!liveStockData?.stock || changeItemSeedKeysRef.current.size === 0) return
    const freshKeys = new Set<string>()
    for (const entry of liveStockData.stock) {
      freshKeys.add(stockLimitKey((entry as any).raw_product_id, (entry as any).branch_id))
    }
    const keysToDrop: string[] = []
    changeItemSeedKeysRef.current.forEach((seedKey) => {
      if (freshKeys.has(seedKey)) keysToDrop.push(seedKey)
    })
    if (keysToDrop.length === 0) return
    keysToDrop.forEach((k) => changeItemSeedKeysRef.current.delete(k))
    setLineStockOverrides(prev => {
      let next: Map<string, any> | null = null
      for (const k of keysToDrop) {
        if (!prev.has(k)) continue
        if (!next) next = new Map(prev)
        next.delete(k)
      }
      return next ?? prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveStockData])

  const effectiveLiveStockData = useMemo(() => {
    if (!liveStockData?.stock) return liveStockData
    if (lineStockOverrides.size === 0) return liveStockData

    const byLine = new Map<string, any>()
    liveStockData.stock.forEach((stockEntry: any) => {
      byLine.set(stockLimitKey(stockEntry.raw_product_id, stockEntry.branch_id), stockEntry)
    })
    lineStockOverrides.forEach((stockEntry, key) => {
      byLine.set(key, stockEntry)
    })

    return {
      ...liveStockData,
      stock: Array.from(byLine.values()),
    }
  }, [liveStockData, lineStockOverrides])

  useEffect(() => {
    if (!effectiveLiveStockData?.stock) return

    const next = new Map(latestStockLimitsRef.current)
    effectiveLiveStockData.stock.forEach((stockEntry: any) => {
      const stockQuantity = stockEntry.stock_quantity ?? 0
      const branchStock = stockEntry.branch_stock ?? stockQuantity
      const dcStock = stockEntry.dc_stock ?? null
      // Phase 4: view-computed cap.
      const maxStock = (stockEntry as any).cap_total ?? stockQuantity

      next.set(stockLimitKey(stockEntry.raw_product_id, stockEntry.branch_id), {
        branchStock,
        maxStock,
        hasDc: dcStock != null,
        // Backorder above the cap on an API-date supplier needs a qty probe
        // (its quoted date is qty-aware); admin-date suppliers don't.
        backorderFromApi:
          stockEntry.backorder_allowed === true && stockEntry.backorder_date_from_api === true,
      })
    })
    latestStockLimitsRef.current = next
  }, [effectiveLiveStockData])

  // Fetch live prices for all cart items
  const rawProductIds = useMemo(() =>
    cart.map(item => Number(item.productId)),
    [cart]
  )

  const { data: livePriceData, isLoading: isLoadingPrice } = useQuery({
    queryKey: ['cart-prices', rawProductIds.join(',')],
    // live: prices must reflect the supplier now, not just "inside the price
    // cycle". A recent answer is still reused, so opening the basket
    // repeatedly does not call the supplier each time.
    queryFn: () => api.getLivePrices(rawProductIds, false, true),
    enabled: cart.length > 0 && rawProductIds.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  })

  // Merge cart items with live stock and price data
  const cartWithStock: CartItem[] = useMemo(() => {
    if (!effectiveLiveStockData?.stock) return cart

    return cart.map(item => {
      // Find stock entry for this item's product + branch combination
      const stockEntry = effectiveLiveStockData.stock.find(
        s => String(s.raw_product_id) === String(item.productId) &&
             String(s.branch_id) === String(item.branchId)
      )

      // Find live price for this item
      const priceEntry = livePriceData?.prices?.find(
        p => String(p.raw_product_id) === String(item.productId)
      )
      const priceUnavailable = priceEntry?.unavailable === true
      const livePrice = (priceEntry?.selling_price && !priceUnavailable)
        ? parseFloat(priceEntry.selling_price)
        : item.price
      // CEF returns a fresh quantity_multiple alongside its live pack price;
      // other suppliers omit it, so the cached catalogue value is preserved.
      const liveMultiple = priceEntry?.quantity_multiple ?? item.quantityMultiple

      // Check if this item was blocked by a role restriction
      const restrictedEntry = effectiveLiveStockData.restricted_items?.find(
        (r: { raw_product_id: number | string; branch_id: string }) =>
          String(r.raw_product_id) === String(item.productId) &&
          String(r.branch_id) === String(item.branchId)
      )
      if (restrictedEntry) {
        return {
          ...item,
          price: livePrice,
          quantityMultiple: liveMultiple,
          priceUnavailable,
          stockUnavailable: true,
          restrictionReason: restrictedEntry.reason,
          restrictionLabel: restrictedEntry.label,
          fulfillmentOptions: [],
        }
      }

      if (!stockEntry) {
        // No stock data for this item - mark as stock unavailable (API failed or no data)
        return {
          ...item,
          price: livePrice,
          quantityMultiple: liveMultiple,
          priceUnavailable,
          stockUnavailable: true,
          fulfillmentOptions: [],
        }
      }

      if ((stockEntry as any).stock_unavailable) {
        return {
          ...item,
          price: livePrice,
          quantityMultiple: liveMultiple,
          priceUnavailable,
          stockUnavailable: true,
          fulfillmentOptions: [],
          maxStock: 0,
          branchStock: 0,
          dcStock: null,
          totalStock: 0,
        }
      }

      const stockQuantity = stockEntry.stock_quantity ?? 0
      // Order-on-Demand: branch vs depot. Cap = max(branch, dc), not sum —
      // defensive against suppliers whose dc_stock may already include the
      // branch portion (e.g. CPS Partial options).
      const dcStock = stockEntry.dc_stock ?? null
      const branchStock = stockEntry.branch_stock ?? null
      const totalStock = stockEntry.total_stock ?? null
      // Phase 4: view-computed cap.
      const dcAwareMax = (stockEntry as any).cap_total ?? stockQuantity
      // Prefer split fulfilment stock from backend; fall back to the combined
      // value so legacy adapters that don't split still render correctly.
      const collectionQty = stockEntry.collection_stock ?? stockQuantity
      const canCollect = stockEntry.can_collect !== false
      const pickupQty = canCollect ? collectionQty : 0
      // Primary delivery follows delivery_stock. dcStock is reserved for the
      // later/DC tier so dc_stock: 0 does not erase branch-backed delivery.
      const canDeliver = stockEntry.can_deliver !== false
      const deliveryQty = !canDeliver
        ? 0
        : (stockEntry.delivery_stock ?? stockQuantity)
      const branchEarliestCollection =
        (stockEntry as any).branch_earliest_collection ?? stockEntry.earliest_collection ?? null
      const branchEarliestDelivery =
        (stockEntry as any).branch_earliest_delivery ?? stockEntry.earliest_delivery ?? null
      const oodEarliestCollection =
        (stockEntry as any).ood_earliest_collection ?? stockEntry.earliest_collection ?? null
      const oodEarliestDelivery =
        (stockEntry as any).ood_earliest_delivery ?? stockEntry.earliest_delivery ?? null
      // Phase 2: explicit per-channel DC dates. Adapter populates only when
      // a real DC date applies; null → frontend shows generic "Later" label.
      const pickupDcDate = (stockEntry as any).pickup_dc_date ?? null
      const deliveryDcDate = (stockEntry as any).delivery_dc_date ?? null
      // Phase 3: explicit per-channel LATER-tile qtys.
      const pickupDcStock = (stockEntry as any).pickup_dc_stock ?? null
      const deliveryDcStock = (stockEntry as any).delivery_dc_stock ?? null
      // Phase 4: view-computed cap.
      const capTotal = (stockEntry as any).cap_total ?? null
      // Per-channel order-placement date. Prefer the branch's own earliest
      // date only when the branch alone can actually fulfil the line qty —
      // otherwise the order really ships from the DC and the branch date is
      // a promise the branch can't keep. When qty exceeds branch stock,
      // fall through to the RAW ood* recheck value (only non-null when the
      // per-line recheck actually landed a fresh date) or the per-channel
      // DC date. This mirrors ProductDetail's `deliveryDisplayDate` rule
      // (SupplierListingCard.tsx:391-394): DC LATER date when qty overflows.
      // The `oodEarliestCollection` / `oodEarliestDelivery` locals above
      // fall back to the initial fetch's branch date when no recheck has
      // landed — read the raw field here to avoid re-surfacing the branch
      // date in the qty-overflow branch. Only use the DC fallback when DC
      // stock actually exists.
      const dcCanProvide = dcStock != null && dcStock > 0
      const rawOodPickupDate = (stockEntry as any).ood_earliest_collection ?? null
      const rawOodDeliveryDate = (stockEntry as any).ood_earliest_delivery ?? null
      // Compare against the explicit branch-only shelf (`branch_stock`),
      // NOT pickupQty/deliveryQty. The latter come from
      // collection_stock/delivery_stock which some adapters (CPS) may
      // report as a pooled option-size at recheck — treating that as
      // branch capacity would pair the merged branch date with the
      // DC-pool qty and re-surface the misleading "today" promise this
      // gating is here to fix. `branchStock` is the definitively
      // branch-only value; fall back to stockQuantity only when the
      // adapter didn't split (legacy path).
      const branchOnlyStock = branchStock ?? stockQuantity
      const branchCanFulfilPickup = canCollect && item.quantity <= branchOnlyStock
      const branchCanFulfilDelivery = canDeliver && item.quantity <= branchOnlyStock
      // Backorder per channel — same capacity rule as getAvailableStock —
      // the line's order date (group header rollup) is the backorder date.
      const lineBackorderAllowed = stockEntry.backorder_allowed === true
      const pickupCapacity = dcStock != null ? dcAwareMax : pickupQty
      const deliveryCapacity = Math.max(deliveryQty, deliveryDcStock ?? 0, dcStock ?? 0)
      const isPickupBackorder =
        lineBackorderAllowed &&
        canCollect &&
        item.quantity > pickupCapacity &&
        stockEntry.backorder_collection_date != null
      const isDeliveryBackorder =
        lineBackorderAllowed &&
        canDeliver &&
        item.quantity > deliveryCapacity &&
        stockEntry.backorder_delivery_date != null
      // Qty exceeds branch stock — the line ships in two tiers (partial:
      // branch portion available today + DC portion later) or entirely from
      // DC. The group header must reflect when the WHOLE line is ready, so
      // pick the LATER of the two candidate dates:
      //   - rawOodPickupDate is the supplier's earliest start (for a
      //     partial that's Today, from the branch portion) — reading it
      //     alone flips the header back to Today after the qty-probe lands.
      //   - pickupDcDate is the DC arrival date (the completion date).
      // ISO date strings compare correctly lexicographically, so a reduce
      // picks the max. Null candidates are filtered out.
      const overflowPickupDate = [
        rawOodPickupDate,
        dcCanProvide ? pickupDcDate : null,
      ]
        .filter((date): date is string => !!date)
        .reduce<string | null>((latest, date) => (latest && latest > date ? latest : date), null)
      const overflowDeliveryDate = [
        rawOodDeliveryDate,
        dcCanProvide ? deliveryDcDate : null,
      ]
        .filter((date): date is string => !!date)
        .reduce<string | null>((latest, date) => (latest && latest > date ? latest : date), null)
      const pickupOrderDate = isPickupBackorder
        ? (stockEntry.backorder_collection_date ?? null)
        : branchCanFulfilPickup
          ? branchEarliestCollection ?? oodEarliestCollection ?? (dcCanProvide ? pickupDcDate : null)
          : overflowPickupDate
      const deliveryOrderDate = isDeliveryBackorder
        ? (stockEntry.backorder_delivery_date ?? null)
        : branchCanFulfilDelivery
          ? branchEarliestDelivery ?? oodEarliestDelivery ?? (dcCanProvide ? deliveryDcDate : null)
          : overflowDeliveryDate
      const fulfillmentOptions: FulfillmentOption[] = [
        {
          type: 'pickup' as const,
          available: pickupQty > 0,
          stockQuantity: pickupQty,
          stockStatus: pickupQty > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: branchEarliestCollection,
        },
        {
          type: 'delivery' as const,
          available: deliveryQty > 0,
          stockQuantity: deliveryQty,
          stockStatus: deliveryQty > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: oodEarliestDelivery,
        },
      ]

      return {
        ...item,
        price: livePrice, // Update with live price
        quantityMultiple: liveMultiple,
        priceUnavailable,
        fulfillmentOptions,
        maxStock: dcAwareMax,
        branchStock,
        dcStock,
        totalStock,
        branchEarliestCollection,
        branchEarliestDelivery,
        oodEarliestCollection,
        oodEarliestDelivery,
        pickupDcDate,
        deliveryDcDate,
        pickupDcStock,
        deliveryDcStock,
        capTotal,
        pickupOrderDate,
        deliveryOrderDate,
        canCollect,
        canDeliver,
        // Backorder tier — backend-decided; absent on older responses = off.
        backorderAllowed: stockEntry.backorder_allowed === true,
        backorderCollectionDate: stockEntry.backorder_collection_date ?? null,
        backorderDeliveryDate: stockEntry.backorder_delivery_date ?? null,
        backorderDateFromApi: stockEntry.backorder_date_from_api === true,
        // DEV-51018: per-channel "is this line actually on backorder right now"
        // — the grouper uses these to split a supplier+branch into an
        // in-stock and a backorder order card.
        isPickupBackorder,
        isDeliveryBackorder,
        // Delivery source branch (present only when the adapter borrowed the
        // delivery figure cross-branch). Cart-only, informational.
        deliverySourceBranchId: (stockEntry as any).delivery_source_branch_id ?? null,
        deliverySourceBranchName: (stockEntry as any).delivery_source_branch_name ?? null,
        deliverySourceBranchDistanceMiles:
          (stockEntry as any).delivery_source_branch_distance_miles ?? null,
        distanceMiles: stockEntry.distance_miles ?? item.distanceMiles,
        // Prefer the supplier's live UOM when the stock response carries
        // one (currently Travis Perkins). Other suppliers leave it null,
        // so the cached catalogue value is preserved.
        unitOfMeasure: stockEntry.uom ?? item.unitOfMeasure,
      }
    })
  }, [cart, effectiveLiveStockData, livePriceData])

  // Self-heal the persisted borrowed delivery source on cart_item after live
  // stock lands. Grouping keys off the delivery source, so if the DB holds
  // stale (or empty) values, the first render of the next cart open would
  // flicker before live stock catches up. Fire-and-forget PATCH keeps the DB
  // in step with what the supplier said this refresh — no backfill needed
  // for pre-existing lines. Guarded on a real stock entry (not the
  // inherited-from-cart placeholder used for missing/unavailable rows) so a
  // failed lookup never wipes a good persisted value.
  useEffect(() => {
    if (isLoadingStock) return
    if (!liveStockData?.stock) return
    cartWithStock.forEach((liveItem) => {
      if (!liveItem.id) return
      if (liveItem.stockUnavailable) return
      const stockEntry = liveStockData.stock.find(
        (entry: { raw_product_id: number | string; branch_id: string | number }) =>
          String(entry.raw_product_id) === String(liveItem.productId) &&
          String(entry.branch_id) === String(liveItem.branchId),
      )
      if (!stockEntry) return

      const persistedItem = cart.find((line) => line.id === liveItem.id)
      if (!persistedItem) return

      const liveBranchId = liveItem.deliverySourceBranchId ?? null
      const persistedBranchId = persistedItem.deliverySourceBranchId ?? null
      if (liveBranchId === persistedBranchId) return

      const signature = `${liveItem.id}::${liveBranchId ?? ''}`
      if (deliverySourceSyncRef.current.has(signature)) return
      deliverySourceSyncRef.current.add(signature)

      updateCartItemDeliverySource(String(liveItem.id), {
        delivery_source_branch_id: liveBranchId,
      })
        .then(() => {
          // Reflect the new persisted state (id + name via live-stock lookup)
          // so this effect no longer sees a diff — otherwise the cart cache
          // stays stale and any re-render would refire the signature check
          // needlessly. Name comes off the live item because the backend
          // response also derives it via JOIN — same value both ways.
          queryClient.setQueryData<CartResponse>(cartQueryKey, (oldData) => {
            if (!oldData) return oldData
            return {
              ...oldData,
              items: oldData.items.map((row: CartItemResponse) =>
                row.id === liveItem.id
                  ? {
                      ...row,
                      delivery_source_branch_id: liveBranchId,
                      delivery_source_branch_name:
                        liveItem.deliverySourceBranchName ?? null,
                    }
                  : row,
              ),
            }
          })
        })
        .catch(() => {
          // Drop the signature so the next render can retry — never let a
          // transient PATCH failure freeze the self-heal for the session.
          deliverySourceSyncRef.current.delete(signature)
        })
    })
    // cartQueryKey excluded — its array identity changes every render but
    // its contents only shift when tenantSlug does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, cartWithStock, isLoadingStock, liveStockData, queryClient, tenantSlug])

  // Resolve an item's fulfillment. Order of precedence:
  //   1. `item.fulfillmentMethod` — persisted on the backend cart line.
  //      Cross-tab / cross-device / refresh-safe. This is what makes the
  //      engineer's PDP or Cart-sheet pick stick.
  //   2. Per-item sessionStorage (BM-015) — kept during the rollout so lines
  //      created before this field shipped, or lines whose backend write
  //      failed transiently, still render under the engineer's choice.
  //   3. Group-level sessionStorage — historical ProductDetail bridge, same
  //      transitional reason. Removed in a follow-up cleanup PR.
  //   4. Default: pickup unless the item can only deliver.
  const getItemFulfillment = (item: CartItem): 'pickup' | 'delivery' => {
    if (item.fulfillmentMethod) return item.fulfillmentMethod
    if (item.id) {
      const perItem = itemFulfillment.get(item.id)
      if (perItem) return perItem
    }
    const groupExisting = fulfillmentByGroup.get(`${item.supplierId}-${item.branchId}`)
    if (groupExisting) return groupExisting
    return item.canCollect !== false ? 'pickup' : 'delivery'
  }

  // With BM-015 the method is baked into the group key, so
  // getGroupFulfillment just reads it off the group. Kept as a helper so
  // downstream callers stay explicit about intent.
  const getGroupFulfillment = (group: BranchGroup): 'pickup' | 'delivery' =>
    group.fulfillmentMethod

  // Quick add keeps a line per branch; when the supplier delivers two lines
  // of one product from the same branch they share a delivery, so merge them
  // into one line (stock then counts the total, the order sends it once).
  // Only on settled live stock, only once the self-heal above has saved each
  // line's delivering branch (so the two never write the same line at once),
  // and never under a line being changed or saved.
  useMergeSameDeliveryLines({
    items: cartWithStock.filter(
      (item) =>
        !item.stockUnavailable &&
        (item.deliverySourceBranchId ?? null) ===
          (cart.find((line) => line.id === item.id)?.deliverySourceBranchId ?? null),
    ),
    getMethod: getItemFulfillment,
    isReady:
      !isLoadingStock &&
      !isFetchingStock &&
      !!liveStockData?.stock &&
      debouncedStockKey === cartStockItemsKey &&
      !isRemoving &&
      !isClearingCart &&
      !isUpdating,
    isLineBusy: (itemId) =>
      editingItemIds.has(itemId) ||
      debouncedUpdateRef.current.has(itemId) ||
      pendingQuantities.has(itemId) ||
      emptyQtyItemIds.has(itemId) ||
      checkingStockItemIds.has(itemId) ||
      changeItem?.id === itemId ||
      itemToRemove?.itemId === itemId ||
      !!manageOrderGroup?.items.some((item) => item.id === itemId),
    mergeCartLines,
    onMerged: (keptItem, removedItemIds) => {
      // The kept line's quantity grew: let the automatic re-check run again
      // so its dates match the new total.
      autoStockCheckKeysRef.current.delete(stockLimitKey(keptItem.product_id, keptItem.branch_id))
      setItemFulfillment((prev) => {
        const next = new Map(prev)
        removedItemIds.forEach((itemId) => next.delete(itemId))
        return next
      })
    },
  })

  // Check if item supports the given fulfillment method
  // Backorder: the supplier/product allows ordering above (or with zero)
  // stock for this channel. Never applies while stock is unknown — a failed
  // stock check stays blocked as before.
  const canBackorder = (item: CartItem, fulfillment: 'pickup' | 'delivery') =>
    item.backorderAllowed === true &&
    !item.stockUnavailable &&
    (fulfillment === 'pickup' ? item.canCollect !== false : item.canDeliver !== false) &&
    // A backorder needs an availability date for the channel.
    (fulfillment === 'pickup'
      ? item.backorderCollectionDate != null
      : item.backorderDeliveryDate != null)

  const supportsSelectedFulfillment = (item: CartItem, fulfillment: 'pickup' | 'delivery') => {
    // If still loading stock, assume available
    if (isLoadingStock) return true
    // Safety check: if fulfillmentOptions is missing or empty, assume not available (stock not fetched)
    if (!item.fulfillmentOptions || !Array.isArray(item.fulfillmentOptions) || item.fulfillmentOptions.length === 0) {
      return true // Still loading or no data
    }
    if (fulfillment === 'pickup' && item.canCollect === false) return false
    if (fulfillment === 'delivery' && item.canDeliver === false) return false
    // Zero stock everywhere is still orderable on backorder.
    if (canBackorder(item, fulfillment)) return true
    const option = item.fulfillmentOptions.find(opt => opt.type === fulfillment)
    if (fulfillment === 'pickup' && item.dcStock != null) {
      return (item.maxStock ?? 0) > 0
    }
    if (fulfillment === 'delivery' && item.dcStock != null && item.dcStock > 0) {
      return true
    }
    return option?.available && option.stockQuantity > 0
  }

  // Get available stock for the given fulfillment method
  const getAvailableStock = (item: CartItem, fulfillment: 'pickup' | 'delivery') => {
    // Safety check: if fulfillmentOptions is missing or empty, use maxStock
    if (!item.fulfillmentOptions || !Array.isArray(item.fulfillmentOptions) || item.fulfillmentOptions.length === 0) {
      return item.maxStock
    }
    if (fulfillment === 'pickup' && item.canCollect === false) return 0
    if (fulfillment === 'delivery' && item.canDeliver === false) return 0
    const option = item.fulfillmentOptions.find(opt => opt.type === fulfillment)
    // Pickup can be fulfilled by branch + DC stock. Delivery is capped by the
    // delivery tier's own stock quantity.
    if (fulfillment === 'pickup' && item.dcStock != null) {
      return item.maxStock
    }
    if (fulfillment === 'delivery') {
      // Match ProductDetail / ChangeItemSheet — DC-backed delivery capacity
      // is orderable, it just ships on the LATER date (deliveryDisplayDate
      // below already switches to the LATER label when qty > primary). CPS
      // borrowed rows report the DC portion in `delivery_dc_stock` while
      // leaving the merged `dc_stock` null, so the earlier `dcStock > 0`
      // guard skipped them and Cart capped at delivery_stock only — user
      // saw "Exceeds stock" for a qty ProductDetail happily accepted.
      const primary = option?.stockQuantity ?? 0
      const dcBackedDelivery = item.deliveryDcStock ?? item.dcStock ?? 0
      const withDc = Math.max(primary, dcBackedDelivery)
      return withDc > 0 ? withDc : (option?.stockQuantity ?? item.maxStock)
    }
    return option?.stockQuantity ?? item.maxStock
  }

  cartWithStockRef.current = cartWithStock

  // DEV-51268: post-edit watch — if a line exited editing while its stock
  // check was still in flight, we're waiting for cartWithStock to reflect
  // the response. When that happens, compare the fresh flag to the
  // pre-edit snapshot and toast on the in-stock -> backorder transition.
  // The 5s expiry timer set in exitEditing handles cleanup if no update
  // ever lands. Placed after cartWithStock so the dep isn't in the TDZ.
  useEffect(() => {
    if (postEditWatchRef.current.size === 0) return
    postEditWatchRef.current.forEach((watch, itemId) => {
      const stockedItem = cartWithStock.find(i => i.id === itemId)
      if (!stockedItem) {
        dropPostEditWatch(itemId)
        return
      }
      const method = getItemFulfillment(stockedItem)
      const nowBackorder = isItemBackorderInMethod(stockedItem, method)
      const wasBackorder = method === 'pickup'
        ? watch.snapshot.isPickupBackorder
        : watch.snapshot.isDeliveryBackorder
      if (nowBackorder && !wasBackorder) {
        addToast('Item moved to Backorder', 'info')
        dropPostEditWatch(itemId)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartWithStock])

  const branchGroups = groupCartByBranch(
    cartWithStock,
    getItemFulfillment,
    // DEV-51268: freeze editing lines to the flags captured when editing
    // began so the row doesn't cross the in-stock / backorder boundary
    // mid-tap. The Set gate is what actually forces a re-group when a line
    // enters or leaves editing state — the ref alone wouldn't re-render.
    (item) => (item.id && editingItemIds.has(item.id) ? editingSnapshotsRef.current.get(item.id) : undefined),
  )

  // Group branch groups by supplier for the render. Preserves the distance-based
  // order of branchGroups — the first supplier in the list is the one with the
  // closest branch overall, and within each supplier its branches stay in
  // distance order.
  const supplierGroups: Array<{
    supplierId: string | number
    supplierName: string
    branches: typeof branchGroups
  }> = []
  const supplierIndex = new Map<string, number>()
  branchGroups.forEach(group => {
    const key = String(group.supplierId)
    let idx = supplierIndex.get(key)
    if (idx === undefined) {
      idx = supplierGroups.length
      supplierIndex.set(key, idx)
      supplierGroups.push({
        supplierId: group.supplierId,
        supplierName: group.supplierName,
        branches: [],
      })
    }
    supplierGroups[idx].branches.push(group)
  })

  // Compute per-group totals using each group's own fulfillment (BM-004), then
  // roll up. There is no longer a single global fulfillment to filter by.
  let totalItems = 0
  let grandTotal = 0
  let hasUnavailablePrice = false
  branchGroups.forEach(group => {
    const groupFulfillment = getGroupFulfillment(group)
    const availableInGroup = group.items.filter(item => supportsSelectedFulfillment(item, groupFulfillment))
    totalItems += availableInGroup.length
    hasUnavailablePrice = hasUnavailablePrice || availableInGroup.some(item => item.priceUnavailable)
    grandTotal += availableInGroup.reduce(
      (sum, item) => sum + (item.priceUnavailable ? 0 : roundToPenny(perUnitPrice(item) * item.quantity)),
      0,
    )
  })

  const cancelLineStockCheck = (lineKey: string, itemId?: string | null) => {
    const existing = stockCheckDebounceRef.current.get(lineKey)
    if (existing) clearTimeout(existing)
    stockCheckDebounceRef.current.delete(lineKey)
    stockCheckSeqRef.current.set(lineKey, (stockCheckSeqRef.current.get(lineKey) ?? 0) + 1)
    setLineStockOverrides(prev => {
      if (!prev.has(lineKey)) return prev
      const next = new Map(prev)
      next.delete(lineKey)
      return next
    })
    if (itemId) {
      setCheckingStockItemIds(prev => {
        if (!prev.has(itemId)) return prev
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
    }
  }

  const scheduleLineStockCheck = (item: CartItem, newQuantity: number, visibleAvailableStock?: number) => {
    const lineKey = stockLimitKey(item.productId, item.branchId)
    const stockLimit = latestStockLimitsRef.current.get(lineKey)
    const checkCap = visibleAvailableStock ?? stockLimit?.maxStock
    const withinDcRange =
      stockLimit?.hasDc === true &&
      newQuantity > stockLimit.branchStock &&
      checkCap != null &&
      newQuantity <= checkCap
    // Backorder above the cap for an API-date supplier: re-probe so the
    // line's backorder date matches the requested qty.
    const backorderAboveCap =
      stockLimit?.backorderFromApi === true &&
      checkCap != null &&
      newQuantity > checkCap
    const shouldCheck =
      (withinDcRange || backorderAboveCap) &&
      item.branchId !== null &&
      !!session?.postcode

    if (!shouldCheck) {
      cancelLineStockCheck(lineKey, item.id)
      return
    }

    const nextSeq = (stockCheckSeqRef.current.get(lineKey) ?? 0) + 1
    stockCheckSeqRef.current.set(lineKey, nextSeq)
    if (item.id) {
      setCheckingStockItemIds(prev => new Set(prev).add(item.id!))
    }

    const existing = stockCheckDebounceRef.current.get(lineKey)
    if (existing) clearTimeout(existing)

    const timer = setTimeout(() => {
      api.getCartStock(
        [{
          raw_product_id: item.productId,
          branch_id: String(item.branchId),
          quantity: newQuantity,
        }],
        session!.postcode,
        session!.latitude,
        session!.longitude,
      )
        .then((response: any) => {
          if (stockCheckSeqRef.current.get(lineKey) !== nextSeq) return
          if (item.id && !cartRef.current.some(cartItem => cartItem.id === item.id)) return

          const stockEntry = response?.stock?.find((entry: any) =>
            String(entry.raw_product_id) === String(item.productId) &&
            String(entry.branch_id) === String(item.branchId)
          )
          const baseStockEntry = liveStockData?.stock?.find((entry: any) =>
            String(entry.raw_product_id) === String(item.productId) &&
            String(entry.branch_id) === String(item.branchId)
          )
          const baseEntry = baseStockEntry ?? stockEntry
          const nextEntry = stockEntry ? {
            ...baseEntry,
            branch_earliest_collection:
              (baseStockEntry as any)?.branch_earliest_collection ??
              (baseStockEntry as any)?.earliest_collection ??
              stockEntry.earliest_collection ??
              null,
            branch_earliest_delivery:
              (baseStockEntry as any)?.branch_earliest_delivery ??
              (baseStockEntry as any)?.earliest_delivery ??
              stockEntry.earliest_delivery ??
              null,
            ood_earliest_collection: stockEntry.earliest_collection ?? null,
            ood_earliest_delivery: stockEntry.earliest_delivery ?? null,
            // LATER-tile quantity is depot capacity — qty-independent — so
            // keep the BASE (initial browse) value. A qty>branch recheck can
            // collapse it (CPS folds the depot into the branch primary and
            // returns pickup_dc_stock=0), which would wrongly hide the LATER
            // tier. The LATER date prefers the qty-specific recheck value
            // (Wolseley/UKPS are qty-aware) and falls back to the base date
            // (CPS collapses pickup_dc_date to null on the recheck).
            pickup_dc_stock:
              (baseStockEntry as any)?.pickup_dc_stock ?? stockEntry.pickup_dc_stock ?? null,
            delivery_dc_stock:
              (baseStockEntry as any)?.delivery_dc_stock ?? stockEntry.delivery_dc_stock ?? null,
            pickup_dc_date:
              stockEntry.pickup_dc_date ?? (baseStockEntry as any)?.pickup_dc_date ?? null,
            delivery_dc_date:
              stockEntry.delivery_dc_date ?? (baseStockEntry as any)?.delivery_dc_date ?? null,
            dc_stock: stockEntry.dc_stock ?? null,
            cap_total: stockEntry.cap_total ?? null,
            // Backorder dates are qty-aware for API-date suppliers — take the
            // probe's values; the flags are qty-independent (base or probe).
            backorder_allowed:
              stockEntry.backorder_allowed ?? baseStockEntry?.backorder_allowed ?? false,
            backorder_date_from_api:
              stockEntry.backorder_date_from_api ??
              baseStockEntry?.backorder_date_from_api ??
              false,
            backorder_collection_date:
              stockEntry.backorder_collection_date ??
              baseStockEntry?.backorder_collection_date ??
              null,
            backorder_delivery_date:
              stockEntry.backorder_delivery_date ??
              baseStockEntry?.backorder_delivery_date ??
              null,
          } : {
            raw_product_id: item.productId,
            branch_id: item.branchId,
            stock_quantity: 0,
            collection_stock: 0,
            delivery_stock: 0,
            branch_stock: 0,
            dc_stock: null,
            total_stock: 0,
            stock_unavailable: true,
            branch_earliest_collection:
              (baseStockEntry as any)?.branch_earliest_collection ??
              (baseStockEntry as any)?.earliest_collection ??
              null,
            branch_earliest_delivery:
              (baseStockEntry as any)?.branch_earliest_delivery ??
              (baseStockEntry as any)?.earliest_delivery ??
              null,
            ood_earliest_collection: null,
            ood_earliest_delivery: null,
          }

          setLineStockOverrides(prev => {
            const next = new Map(prev)
            next.set(lineKey, nextEntry)
            return next
          })
        })
        .catch(() => {
          if (stockCheckSeqRef.current.get(lineKey) !== nextSeq) return
          setLineStockOverrides(prev => {
            const next = new Map(prev)
            next.set(lineKey, {
              raw_product_id: item.productId,
              branch_id: item.branchId,
              stock_quantity: 0,
              collection_stock: 0,
              delivery_stock: 0,
              branch_stock: 0,
              dc_stock: null,
              total_stock: 0,
              stock_unavailable: true,
            })
            return next
          })
        })
        .finally(() => {
          if (stockCheckSeqRef.current.get(lineKey) !== nextSeq) return
          stockCheckDebounceRef.current.delete(lineKey)
          if (item.id) {
            setCheckingStockItemIds(prev => {
              const next = new Set(prev)
              next.delete(item.id!)
              return next
            })
          }
        })
    }, 600)

    stockCheckDebounceRef.current.set(lineKey, timer)
  }

  useEffect(() => {
    if (!liveStockData?.stock || !session?.postcode) return

    cartWithStock.forEach(item => {
      const lineKey = stockLimitKey(item.productId, item.branchId)
      const stockLimit = latestStockLimitsRef.current.get(lineKey)
      const method = getItemFulfillment(item)
      const visibleAvailableStock = getAvailableStock(item, method)
      const visibleQuantity = item.id ? (pendingQuantities.get(item.id) ?? item.quantity) : item.quantity

      if (visibleQuantity > visibleAvailableStock) {
        // Above the cap. For an API-date backorder line the qty-aware probe
        // must survive (it refreshes the backorder date) — schedule it once
        // per line/qty instead of cancelling it. Everything else keeps the
        // original cancel (an over-stock qty has nothing to check).
        const backorderProbe =
          stockLimit?.backorderFromApi === true && canBackorder(item, method)
        if (!backorderProbe) {
          cancelLineStockCheck(lineKey, item.id)
          autoStockCheckKeysRef.current.delete(lineKey)
          return
        }
        const probeKey = `${lineKey}|${visibleQuantity}`
        if (autoStockCheckKeysRef.current.has(probeKey)) return
        autoStockCheckKeysRef.current.add(probeKey)
        scheduleLineStockCheck(item, visibleQuantity, visibleAvailableStock)
        return
      }

      const shouldAutoCheck =
        stockLimit?.hasDc === true &&
        visibleQuantity > stockLimit.branchStock &&
        visibleQuantity <= visibleAvailableStock &&
        item.branchId !== null

      if (!shouldAutoCheck || autoStockCheckKeysRef.current.has(lineKey)) return

      autoStockCheckKeysRef.current.add(lineKey)
      scheduleLineStockCheck(item, visibleQuantity, visibleAvailableStock)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartWithStock, liveStockData, session?.postcode, fulfillmentByGroup, itemFulfillment, pendingQuantities])

  // DEV-51268: drop a post-edit watch (if any) and clear its expiry timer.
  // Safe to call for an id that isn't being watched.
  const dropPostEditWatch = (itemId: string) => {
    const watch = postEditWatchRef.current.get(itemId)
    if (!watch) return
    clearTimeout(watch.timerId)
    postEditWatchRef.current.delete(itemId)
  }

  // DEV-51268: enter editing state for a line — snapshot its current
  // backorder flags (once per burst) and (re)set the exit debounce so the
  // row stays pinned to its pre-edit group until the engineer settles.
  // Starting a fresh edit voids any post-edit watch for this line so the
  // pre-edit snapshot doesn't linger and toast against an unrelated flip.
  const beginEditing = (itemId: string) => {
    dropPostEditWatch(itemId)
    if (!editingSnapshotsRef.current.has(itemId)) {
      const stockedItem = cartWithStockRef.current.find(i => i.id === itemId)
      editingSnapshotsRef.current.set(itemId, {
        isPickupBackorder: stockedItem?.isPickupBackorder === true,
        isDeliveryBackorder: stockedItem?.isDeliveryBackorder === true,
      })
      setEditingItemIds(prev => {
        if (prev.has(itemId)) return prev
        const next = new Set(prev)
        next.add(itemId)
        return next
      })
    }
    const existing = exitEditingTimersRef.current.get(itemId)
    if (existing) clearTimeout(existing)
    exitEditingTimersRef.current.set(itemId, setTimeout(() => {
      exitEditing(itemId)
    }, 800))
  }

  // DEV-51268: leave editing state for a line — drop the snapshot and, if
  // the line's live flags now place it on backorder for its chosen channel
  // while the snapshot did not, toast the engineer so the newly-moved row
  // isn't a silent surprise. Reads live state via cartWithStockRef because
  // the stock check that flips the flag often lands AFTER the last tap.
  // If the stock check hasn't landed yet (still not backorder), hand off
  // to a 5s post-edit watch so a later flip still fires the toast.
  const exitEditing = (itemId: string) => {
    const snapshot = editingSnapshotsRef.current.get(itemId)
    editingSnapshotsRef.current.delete(itemId)
    exitEditingTimersRef.current.delete(itemId)
    setEditingItemIds(prev => {
      if (!prev.has(itemId)) return prev
      const next = new Set(prev)
      next.delete(itemId)
      return next
    })
    if (!snapshot) return
    const stockedItem = cartWithStockRef.current.find(i => i.id === itemId)
    if (!stockedItem) return
    const method = getItemFulfillment(stockedItem)
    const nowBackorder = isItemBackorderInMethod(stockedItem, method)
    const wasBackorder = method === 'pickup'
      ? snapshot.isPickupBackorder
      : snapshot.isDeliveryBackorder
    if (nowBackorder && !wasBackorder) {
      addToast('Item moved to Backorder', 'info')
      return
    }
    // Snapshot said in-stock and the line is still in-stock. The relevant
    // stock check may still be in flight — watch cartWithStock for up to
    // 5s so a delayed flip still triggers the notification.
    if (!wasBackorder && !nowBackorder) {
      dropPostEditWatch(itemId)
      const timerId = setTimeout(() => {
        postEditWatchRef.current.delete(itemId)
      }, 5000)
      postEditWatchRef.current.set(itemId, { snapshot, timerId })
    }
  }

  // Update item quantity with debouncing for instant UI
  const handleUpdateQuantity = (itemId: string, newQuantity: number, visibleAvailableStock?: number) => {
    // If quantity is 0, show removal confirmation instead (ORD-005 edge case)
    if (newQuantity === 0) {
      const item = cart.find(i => i.id === itemId)
      if (item) {
        setItemToRemove({
          itemId,
          productName: item.productName
        })
      }
      return
    }

    // DEV-51268: pin this line to its current group while the engineer is
    // still tapping. Runs on every change so a rapid +/+ / typing burst
    // extends the editing window instead of ending it early.
    beginEditing(itemId)

    const cartItem = cart.find(i => i.id === itemId)

    // Over-cap guard: when the entered qty exceeds what's orderable, keep
    // it visible (so the "exceeds stock" warning shows) but do NOT sync to
    // the backend or run a stock check. Syncing an invalid qty makes the
    // backend clamp it and the cart refetch snap the input back to the old
    // value while the live-stock spinner spins. We also cancel any pending
    // sync / auto-clear from a prior valid change so the invalid value
    // stays put until the engineer corrects it.
    // The raw cart line carries no live-stock fields — read the backorder
    // flags from the stock-merged line, otherwise a backorder qty is never
    // synced (stays local-only, header/totals keep the old qty).
    const stockedItem = cartWithStock.find(i => i.id === itemId)
    const cap =
      stockedItem && canBackorder(stockedItem, getItemFulfillment(stockedItem))
        ? Infinity // backorder: any qty (up to the selector max) is orderable
        : visibleAvailableStock ??
          cartItem?.capTotal ??
          cartItem?.maxStock ??
          Infinity
    if (newQuantity > cap) {
      if (cartItem) {
        cancelLineStockCheck(
          stockLimitKey(cartItem.productId, cartItem.branchId),
          itemId,
        )
      }
      const pendingSync = debouncedUpdateRef.current.get(itemId)
      if (pendingSync) {
        clearTimeout(pendingSync)
        debouncedUpdateRef.current.delete(itemId)
      }
      const pendingClear = pendingQuantityClearRef.current.get(itemId)
      if (pendingClear) {
        clearTimeout(pendingClear)
        pendingQuantityClearRef.current.delete(itemId)
      }
      setPendingQuantities(prev => {
        const next = new Map(prev)
        next.set(itemId, newQuantity)
        return next
      })
      return
    }

    if (cartItem) {
      autoStockCheckKeysRef.current.delete(stockLimitKey(cartItem.productId, cartItem.branchId))
      scheduleLineStockCheck(cartItem, newQuantity, visibleAvailableStock)
    }

    setPendingQuantities(prev => {
      const next = new Map(prev)
      next.set(itemId, newQuantity)
      return next
    })
    const existingPendingClear = pendingQuantityClearRef.current.get(itemId)
    if (existingPendingClear) clearTimeout(existingPendingClear)
    const pendingClearTimer = setTimeout(() => {
      setPendingQuantities(prev => {
        if (!prev.has(itemId)) return prev
        const next = new Map(prev)
        next.delete(itemId)
        return next
      })
      pendingQuantityClearRef.current.delete(itemId)
    }, 8000)
    pendingQuantityClearRef.current.set(itemId, pendingClearTimer)

    // Analytics: record the pre-edit quantity before the optimistic write
    // below overwrites it. Only on the first tap of a burst — a later tap in
    // the same burst must not overwrite the original value.
    if (cartItem && !quantityBeforeChangeRef.current.has(itemId)) {
      quantityBeforeChangeRef.current.set(itemId, cartItem.quantity)
    }

    // Update cart in React Query cache immediately (instant UI)
    queryClient.setQueryData(cartQueryKey, (oldData: any) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        items: oldData.items.map((item: any) =>
          item.id === itemId ? { ...item, quantity: newQuantity } : item
        ),
      }
    })

    // Debounce backend sync (wait 500ms)
    const existing = debouncedUpdateRef.current.get(itemId)
    if (existing) clearTimeout(existing)

    const timer = setTimeout(() => {
      const quantityBefore = quantityBeforeChangeRef.current.get(itemId)
      quantityBeforeChangeRef.current.delete(itemId)
      updateCartItem(itemId, newQuantity, quantityBefore)  // Send to backend
      debouncedUpdateRef.current.delete(itemId)
    }, 500)

    debouncedUpdateRef.current.set(itemId, timer)
  }

  // Remove item from cart (now uses backend cart item ID)
  const confirmRemoveItem = async () => {
    if (!itemToRemove) return

    const removedId = itemToRemove.itemId
    // Close dialog immediately — the mutation is optimistic and the
    // cache already reflects the removal for the sub-second the network
    // call is in flight.
    setItemToRemove(null)

    try {
      await removeCartItemAsync(removedId)
    } catch {
      // Mutation's onError has already rolled the cart snapshot back,
      // so the line reappears in the UI. Keep the fulfillment override
      // intact so the restored line renders under the correct method.
      addToast('Couldn\'t remove item — please try again', 'error')
      return
    }

    // BM-015: only drop the per-line fulfillment override once the
    // delete has committed. If we cleared it earlier and the delete
    // rolled back, the restored line would silently switch to the
    // group-level fallback (potentially delivery -> pickup) — the
    // engineer would see their explicit per-item choice vanish for no
    // apparent reason.
    setItemFulfillment(prev => {
      if (!prev.has(removedId)) return prev
      const next = new Map(prev)
      next.delete(removedId)
      return next
    })
    removeItemFulfillmentMethod(removedId)

    addToast('Item removed from basket', 'success', 2000)
  }

  const confirmClearCart = async () => {
    setIsClearingCart(true)
    // Cancel every pending debounced write BEFORE deleting the cart. A quantity
    // change made within the 500ms debounce window would otherwise fire its
    // updateCartItem after clearCart(): the mutation snapshots the still-
    // populated cart, 404s, and rolls the cleared lines back into the query
    // cache. Mirrors the unmount cleanup, plus resets the pending qty state.
    debouncedUpdateRef.current.forEach((timer) => clearTimeout(timer))
    debouncedUpdateRef.current.clear()
    pendingQuantityClearRef.current.forEach((timer) => clearTimeout(timer))
    pendingQuantityClearRef.current.clear()
    stockCheckDebounceRef.current.forEach((timer) => clearTimeout(timer))
    stockCheckDebounceRef.current.clear()
    setPendingQuantities(new Map())
    setEmptyQtyItemIds(new Set())
    try {
      await clearCart({ trackClear: true })
      // BM-015: wipe the per-line fulfillment overrides for the cleared
      // ids. Group-level bridge is kept so ProductDetail's on-add pick
      // still resolves for a subsequent restart of shopping.
      const clearedIds = cartRef.current.map((item) => item.id).filter((id): id is string => !!id)
      if (clearedIds.length > 0) {
        setItemFulfillment(prev => {
          if (prev.size === 0) return prev
          const next = new Map(prev)
          for (const id of clearedIds) next.delete(id)
          return next
        })
        for (const id of clearedIds) removeItemFulfillmentMethod(id)
      }
      addToast('Basket cleared', 'success', 2000)
      setShowClearConfirm(false)
    } catch {
      addToast('Failed to clear basket', 'error')
    } finally {
      setIsClearingCart(false)
    }
  }

  // BM-014: Apply changes from the Manage Order sheet.
  //
  // Runs on Update-in-sheet. Moves every item in the group to the new
  // branch when Collection branch changed, then updates the per-group
  // fulfillment method (state + sessionStorage bridge). 409s from
  // updateCartLine (product already at target branch) are toasted per
  // item so the rest of the group still moves — best-effort semantics
  // match the mock's atomic-feeling UI without demanding a transaction.
  const applyManageOrder = async ({ newBranchId, newMethod }: ManageOrderApplyResult) => {
    const group = manageOrderGroup
    if (!group) return
    const currentBranchIdStr = group.branchId != null ? String(group.branchId) : null
    const willChangeBranch =
      newMethod === 'pickup' && newBranchId != null && newBranchId !== currentBranchIdStr

    // Every item in the group flips to `newMethod`, and (when relevant) also
    // moves branch. Both are sent in a single updateCartLine per item so the
    // backend sees the pair atomically and we don't leave a window where a
    // line's branch changed but method didn't — which would otherwise stack
    // an extra PATCH per line.
    for (const item of group.items) {
      if (!item.id) continue
      try {
        await updateCartLine(item.id, {
          ...(willChangeBranch && newBranchId ? { branchId: newBranchId } : {}),
          fulfillmentMethod: newMethod,
        })
      } catch {
        addToast(
          willChangeBranch
            ? `Couldn't move "${item.productName}" — it may already be in your basket at that branch.`
            : `Couldn't update "${item.productName}".`,
          'error',
        )
      }
    }

    const effectiveBranchId = willChangeBranch ? newBranchId : currentBranchIdStr
    // Transitional sessionStorage writes — see Cart.tsx getItemFulfillment
    // precedence. Removed in the follow-up cleanup PR once every deployed
    // client is on the backend-truth path.
    setFulfillmentByGroup(prev => {
      const next = new Map(prev)
      next.set(`${group.supplierId}-${effectiveBranchId}`, newMethod)
      return next
    })
    writeCartFulfillmentMethod(group.supplierId, effectiveBranchId, newMethod)
    setItemFulfillment(prev => {
      const next = new Map(prev)
      for (const item of group.items) {
        if (item.id) next.set(item.id, newMethod)
      }
      return next
    })
    for (const item of group.items) {
      if (item.id) writeItemFulfillmentMethod(item.id, newMethod)
    }
  }

  // BM-015: Apply changes from the Change Item sheet.
  //
  // Two flavours of change:
  //   - Same (product, branch): quantity and/or method — update in place
  //     via updateCartLine + writeItemFulfillmentMethod. No line churn,
  //     analytics fires the usual quantity-updated event.
  //   - Supplier / branch / product change: the underlying raw product
  //     changes, so we remove the old line and add a new one. The new
  //     line's id (returned by addToCartAsync) is what
  //     writeItemFulfillmentMethod stamps so the method survives the
  //     swap and lands on the fresh line the moment the query cache
  //     invalidates.
  const applyChangeItem = async (change: ChangeItemApplyResult) => {
    const productChanged =
      String(change.newProductId) !== String(change.originalProductId) ||
      String(change.newBranchId) !== String(change.originalBranchId ?? '') ||
      String(change.newSupplierId) !== String(change.originalSupplierId)

    // Apply from Change Item is an explicit engineer confirmation for
    // this line — drop any lingering pendingQuantities entry (an
    // over-cap qty typed on the cart card that never synced to the
    // cache) and its auto-clear timer so the card reflects the applied
    // value immediately. Without this, applying qty=3 while pending=5
    // leaves the card rendering 5 until the 8s timer expires.
    const clearPendingQuantityFor = (itemId: string) => {
      const timer = pendingQuantityClearRef.current.get(itemId)
      if (timer) {
        clearTimeout(timer)
        pendingQuantityClearRef.current.delete(itemId)
      }
      setPendingQuantities(prev => {
        if (!prev.has(itemId)) return prev
        const next = new Map(prev)
        next.delete(itemId)
        return next
      })
    }

    if (!productChanged) {
      // Fold method into the same PATCH as quantity when the qty changed —
      // one round-trip, one authoritative response. When only the method
      // changed we still fire a method-only PATCH so the backend row is the
      // truth cross-tab, not just the sessionStorage stamp below.
      const quantityChanged = change.newQuantity !== changeItem?.quantity
      if (quantityChanged || change.newMethod !== changeItem?.fulfillmentMethod) {
        try {
          await updateCartLine(change.originalItemId, {
            ...(quantityChanged ? { quantity: change.newQuantity } : {}),
            fulfillmentMethod: change.newMethod,
          })
        } catch {
          addToast(`Couldn't update "${changeItem?.productName ?? 'item'}"`, 'error')
          return
        }
      }
      setItemFulfillment(prev => {
        const next = new Map(prev)
        next.set(change.originalItemId, change.newMethod)
        return next
      })
      writeItemFulfillmentMethod(change.originalItemId, change.newMethod)
      clearPendingQuantityFor(change.originalItemId)
      return
    }

    // Detect a target-line collision. The cart's product+branch upsert
    // means we can never actually have two separate lines for the same
    // (product, branch) — a POST to that target merges into whichever
    // line already exists.
    const targetLine = cart.find(
      (line) =>
        line.id !== change.originalItemId &&
        String(line.productId) === String(change.newProductId) &&
        String(line.branchId ?? '') === String(change.newBranchId),
    )

    // Reject cross-method collisions. If the target already has this
    // (product, branch) under a different fulfillment method, merging
    // into it would silently convert the target's existing units to the
    // source's chosen method — the engineer only asked to move the
    // source item. Bail out with a clear toast so they can delete the
    // conflicting line first or align methods.
    if (targetLine?.id) {
      const targetMethod = getItemFulfillment(targetLine)
      if (targetMethod !== change.newMethod) {
        addToast(
          `Already have this product at that branch as ${targetMethod === 'pickup' ? 'collection' : 'delivery'}. Remove that line first or match the method.`,
          'error',
        )
        return
      }
    }

    // Add-then-remove: keep the original line in the cart until the
    // replacement is confirmed. If the add fails (stale listing,
    // permissions, transient error) the source stays intact and the
    // engineer just sees the toast — no basket loss. When a same-method
    // target exists, merge into it via updateCartLine so we preserve
    // both quantities instead of letting addToCart's upsert overwrite.
    let createdItemId: string | null = null
    const originalTargetQuantity = targetLine?.quantity ?? null
    try {
      if (targetLine?.id) {
        const mergedQuantity = targetLine.quantity + change.newQuantity
        await updateCartLine(targetLine.id, {
          quantity: mergedQuantity,
          fulfillmentMethod: change.newMethod,
        })
        createdItemId = targetLine.id
      } else {
        const created = await addToCartAsync({
          product_id: String(change.newProductId),
          branch_id: String(change.newBranchId),
          quantity: change.newQuantity,
          // Persist the engineer's pickup/delivery pick on the fresh line
          // so it survives cross-tab and doesn't rely on the transitional
          // sessionStorage stamp we still write below.
          fulfillment_method: change.newMethod,
        })
        createdItemId = created?.id ?? null
      }
    } catch {
      addToast('Couldn\'t add the replacement — original item kept in basket.', 'error')
      return
    }

    // Seed the sheet's already-loaded live-stock data into
    // lineStockOverrides for the new (product, branch) so the cart header
    // renders the correct delivery source branch + dates the instant the
    // sheet closes — no flicker to the pickup branch while the debounced
    // cart-stock refetch (600ms + roundtrip) catches up. Only seed for a
    // fresh add: merging into an existing target line means that line's
    // stock is already in effectiveLiveStockData, and our seed would
    // shadow potentially fresher data with the sheet's snapshot.
    if (!targetLine?.id) {
      const seedKey = stockLimitKey(change.newProductId, change.newBranchId)
      changeItemSeedKeysRef.current.add(seedKey)
      setLineStockOverrides(prev => {
        const next = new Map(prev)
        next.set(seedKey, change.newStockSeed)
        return next
      })
    }

    // Now drop the source line. On success, clean up the source's
    // fulfillment override. On failure, we roll back what we can so the
    // cart doesn't sit with duplicated units.
    let removeSucceeded = true
    try {
      await removeCartItemAsync(change.originalItemId)
    } catch {
      removeSucceeded = false
    }

    if (!removeSucceeded) {
      // For a merge, subtract the units we added back so the target
      // returns to its original quantity — otherwise the cart shows
      // (source + merged) instead of the intended move. For a fresh
      // add there's no clean rollback (deleting the new line would
      // also fail intermittently), so we surface the mess and let the
      // engineer resolve it manually.
      if (targetLine?.id && originalTargetQuantity != null) {
        try {
          await updateCartLine(targetLine.id, { quantity: originalTargetQuantity })
        } catch {
          // Best effort; the toast below is enough context.
        }
        addToast('Move failed — couldn\'t remove the old item, no changes applied.', 'error')
      } else {
        addToast(
          'Added the replacement, but couldn\'t remove the old item — please delete it.',
          'error',
        )
      }
      // Keep the source's fulfillment override intact so the restored
      // line renders under its original method (same reason as Fix 4
      // for confirmRemoveItem).
      return
    }

    // Remove committed — safe to drop the source line's per-line map entry.
    setItemFulfillment(prev => {
      if (!prev.has(change.originalItemId)) return prev
      const next = new Map(prev)
      next.delete(change.originalItemId)
      return next
    })
    removeItemFulfillmentMethod(change.originalItemId)

    if (createdItemId) {
      setItemFulfillment(prev => {
        const next = new Map(prev)
        next.set(createdItemId as string, change.newMethod)
        return next
      })
      writeItemFulfillmentMethod(createdItemId, change.newMethod)
    }

    // Source line is gone; drop its orphaned pending entry. When merged
    // into a target, also clear the target's pending — the sheet just
    // wrote a new merged quantity so any stale over-cap value on the
    // target would render the card at the wrong number.
    clearPendingQuantityFor(change.originalItemId)
    if (targetLine?.id) clearPendingQuantityFor(targetLine.id)
  }

  // Checkout single branch
  const checkoutBranch = (group: BranchGroup) => {
    const profileIncomplete = !meData?.first_name || !meData?.last_name || !meData?.phone
    if (profileIncomplete) {
      setShowProfileDialog(true)
      return
    }
    // Carry this group's exact item ids + its (delivering, on the delivery tab)
    // branch so checkout builds one order from precisely these items — a
    // delivery group can hold items originally picked from different branches.
    const itemIds = group.items
      .map(i => i.id)
      .filter((id): id is string => !!id)
    // DEV-51018: mark the backorder sibling on the URL so Checkout can scope
    // its own checkout_id independently of the in-stock sibling for the same
    // supplier+branch — otherwise the two share one checkout_started and one
    // checkout_abandoned event and the analytics funnel entangles them.
    const params = new URLSearchParams({
      mode: getGroupFulfillment(group),
      ...(group.branchId && { branch: String(group.branchId) }),
      ...(group.isBackorder && { bo: '1' }),
      ...(itemIds.length > 0 && { items: itemIds.join(',') }),
    })
    navigate(`/${tenantSlug}/engineer/checkout?${params}`)
  }

  // Price change badge component
  const PriceChangeBadge = ({ priceAtAdded, currentPrice }: { priceAtAdded: number; currentPrice: number }) => {
    // Round to 2 decimals to avoid floating point issues
    const priceAtAddedRounded = Math.round(priceAtAdded * 100) / 100
    const currentPriceRounded = Math.round(currentPrice * 100) / 100

    // No change if prices are same when rounded
    if (priceAtAddedRounded === currentPriceRounded) return null

    const isIncrease = currentPriceRounded > priceAtAddedRounded

    return (
      <div className={clsx(
        'flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium',
        isIncrease ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
      )}>
        {isIncrease ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        <span>Was £{priceAtAddedRounded.toFixed(2)} → Now £{currentPriceRounded.toFixed(2)}</span>
      </div>
    )
  }

  if (!session) {
    return null
  }

  return (
    <EngineerLayout hideHeader>
      {/* Header — themed via `.bg-nav` / `.text-nav`. The shared
       * EngineerHeader (postcode + gauge) is suppressed on Cart per the
       * mockup: drill-in pages carry only their own back+title bar. */}
      <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => navigate(`/${tenantSlug}/engineer/search`)}
                className="p-2 -ml-2 text-nav hover:bg-nav-hover rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-nav font-bold">
                Basket {cart.length > 0 && `(${totalItems} item${totalItems !== 1 ? 's' : ''})`}
              </h1>
            </div>
            {cart.length > 0 && (
              <button
                onClick={() => setShowClearConfirm(true)}
                className="flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
                aria-label="Clear basket"
              >
                <Trash2 className="w-4 h-4" />
                Clear basket
              </button>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-4 pb-28">
        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-3" />
            <p className="text-gray-500 text-sm">Loading basket…</p>
          </div>
        ) : cart.length === 0 ? (
          // Empty State
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6">
              <ShoppingCart className="w-10 h-10 text-gray-300" />
            </div>
            <h2 className="text-gray-900 text-xl font-bold mb-3 text-center">
              Your basket is empty
            </h2>
            <p className="text-gray-500 text-base text-center mb-6 max-w-sm">
              Start adding items from the search page to place an order
            </p>
            <Button onClick={() => navigate(`/${tenantSlug}/engineer/search`)} size="lg">
              <Package className="w-5 h-5 mr-2" />
              Browse products
            </Button>
          </div>
        ) : (
          <>
            {/* BM-004: the global fulfillment toggle has moved into each
                branch group below. Loading state is surfaced per-item in the
                fulfillment tiles. */}
            {(isLoadingStock || isLoadingPrice) && (
              <div className="flex items-center justify-end mb-3">
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <div className="animate-spin h-3 w-3 border-2 border-gray-300 border-t-brand-primary rounded-full" />
                  {isLoadingStock && isLoadingPrice ? 'Checking stock & price…' : isLoadingStock ? 'Checking stock…' : 'Checking price…'}
                </span>
              </div>
            )}

            {/* Savings basket banner - Hidden for Round 1.
                When re-enabling: per BM-004 there is no global fulfillment
                anymore; decide how SmartCart should be primed (e.g. default to
                'pickup', or infer from the majority of branch groups). */}
            {/*
            <button
              onClick={() => navigate(`/${tenantSlug}/engineer/cart/smart`, {
                state: { fulfillment: 'pickup' }
              })}
              className="w-full bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-4 mb-4 hover:border-green-500 hover:shadow-md transition-all active:scale-[0.99]"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-green-900 font-bold text-sm">
                      View savings basket
                    </div>
                    <div className="text-green-700 text-xs">
                      Find better prices & optimize your order
                    </div>
                  </div>
                </div>
                <ArrowLeft className="w-5 h-5 text-green-600 rotate-180" />
              </div>
            </button>
            */}

            {/* Branch Groups, grouped by supplier. Each supplier is a section
                showing name + product/order summary; within a supplier, branch
                cards split into Collection and Delivery subsections with
                labeled headers (BM-012). */}
            <div className="space-y-6">
              {supplierGroups.map((supplier) => {
                const totalProducts = supplier.branches.reduce(
                  (sum, branch) => sum + branch.items.length,
                  0,
                )
                const totalOrders = supplier.branches.length
                return (
                <div key={String(supplier.supplierId)}>
                  <div>
                    <h2 className="text-gray-950 font-bold text-lg">
                      {supplier.supplierName}
                    </h2>
                    <p className="text-xs text-gray-600 mt-1 mb-4">
                      <span className="text-gray-700">
                        {totalProducts} product{totalProducts !== 1 ? 's' : ''}
                      </span>
                      {' · '}
                      <span className="text-gray-700">
                        {totalOrders} order{totalOrders !== 1 ? 's' : ''}
                      </span>
                    </p>
                  </div>
                  <div className="space-y-6">
                    {/* DEV-51018: sections under each supplier are Pickup,
                        Delivery, then Backorder — in-stock groups sit
                        under their fulfilment method; every backorder
                        group (both pickup and delivery) collects into a
                        single "Backorder" section beneath. */}
                    {(['pickup', 'delivery', 'backorder'] as const).map((section) => {
                      const methodBranches = supplier.branches.filter((branch) =>
                        section === 'backorder'
                          ? branch.isBackorder
                          : !branch.isBackorder && getGroupFulfillment(branch) === section,
                      )
                      if (methodBranches.length === 0) return null
                      return (
                        <div key={section}>
                          <div className="flex items-center gap-2 mb-3">
                            {section === 'pickup' && <Package className="w-4 h-4 text-gray-700" />}
                            {section === 'delivery' && <Truck className="w-4 h-4 text-gray-700" />}
                            {section === 'backorder' && <Hourglass className="w-4 h-4 text-gray-700" />}
                            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                              {section === 'pickup' && 'Collection'}
                              {section === 'delivery' && 'Delivery'}
                              {section === 'backorder' && 'Backorder'}
                            </h3>
                          </div>
                          <div className="space-y-3">
              {methodBranches.map((group) => {
                const groupFulfillment = getGroupFulfillment(group)
                // Check if branch has items available for the group's fulfillment
                const availableInBranch = group.items.filter(item => supportsSelectedFulfillment(item, groupFulfillment))
                const hasAvailableItems = availableInBranch.length > 0
                const branchItemCount = availableInBranch.length
                // Check if any items exceed available stock. Use the effective
                // (pending) quantity — same value the per-line "Exceeds stock"
                // warning reads (see itemQuantity below) — so an over-cap entry
                // that's intentionally kept unsynced (over-cap guard) disables
                // Checkout instead of leaving it enabled against the warning.
                const hasOverStockItems = availableInBranch.some(item => {
                  if (canBackorder(item, groupFulfillment)) return false
                  const availableStock = getAvailableStock(item, groupFulfillment)
                  const effectiveQty = item.id
                    ? (pendingQuantities.get(item.id) ?? item.quantity)
                    : item.quantity
                  return effectiveQty > availableStock
                })
                const hasBranchUnavailablePrice = availableInBranch.some(item => item.priceUnavailable)
                const branchTotal = availableInBranch.reduce(
                  (sum, item) => sum + (item.priceUnavailable ? 0 : roundToPenny(perUnitPrice(item) * item.quantity)),
                  0
                )

                // Check if branch is inactive
                const isBranchInactive = group.items.some(item => item.branchIsActive === false)
                const hasRestrictedItems = group.items.some(item => !!item.restrictionReason)
                const hasEmptyQty = group.items.some(item => item.id && emptyQtyItemIds.has(item.id))
                // Products sold in fixed multiples must sit on a whole multiple —
                // the quantity is sent to the supplier as-is. Block checkout when
                // any line is off its multiple (e.g. added before its pack size
                // was set, or the multiple changed since).
                const hasInvalidMultipleItems = availableInBranch.some(item => {
                  const multiple = item.quantityMultiple ?? 1
                  const effectiveQty = item.id
                    ? (pendingQuantities.get(item.id) ?? item.quantity)
                    : item.quantity
                  return multiple > 1 && effectiveQty % multiple !== 0
                })

                // BM-013 AC2 / DEV-51011 AC12: the header shows the day checkout
                // will book — the latest date across the lines that will be
                // placed, moved on to an open day — from the same helper the
                // checkout row uses. No line date → no date, as before.
                const groupMaxDateLabel = formatEarliestDate(
                  basketGroupDate(
                    availableInBranch,
                    groupFulfillment,
                    group.items[0]?.preferredDate?.supported === true,
                  ),
                )

                // For delivery groups, show the branch that actually delivers
                // (CPS/Wolseley cross-branch borrow). Only claim a single
                // source when EVERY item agrees on it — CPS/Wolseley pick
                // borrow source per-product, so two items at the same
                // requested branch can borrow from different sources. Null
                // deliverySourceBranchName explicitly means "delivered from
                // this row's own branch" (i.e. the group's pickup branch),
                // so it counts as its own distinct source in the agreement
                // check — a group mixing null-source items with a borrowed
                // item is a mixed group and must not be labeled with the
                // borrowed source alone. Display-only — group.branchId still
                // points at the pickup branch for backend ops.
                let headerBranchName = group.branchName
                let headerDistanceMiles = group.distanceMiles
                if (groupFulfillment === 'delivery') {
                  const sources = new Set<string>()
                  let sourceItem: CartItem | undefined
                  for (const it of group.items) {
                    // Effective source for the agreement check: the item's
                    // borrowed source if it has one, else the group's own
                    // (pickup) branch name.
                    const effective = it.deliverySourceBranchName ?? group.branchName
                    sources.add(effective)
                    // Prefer an item that carries a borrowed source so the
                    // display distance matches the borrowed branch's distance
                    // (rather than the group's pickup distance).
                    if (it.deliverySourceBranchName && !sourceItem) sourceItem = it
                  }
                  if (sources.size === 1) {
                    // Every item agrees on one source (all borrowed from the
                    // same branch, OR all delivered from their own branch).
                    if (sourceItem) {
                      // Borrowed source — use its name and distance.
                      headerBranchName = sourceItem.deliverySourceBranchName ?? group.branchName
                      headerDistanceMiles =
                        sourceItem.deliverySourceBranchDistanceMiles ?? group.distanceMiles
                    }
                    // else: sourceItem undefined → every item was null-source →
                    // headerBranchName/DistanceMiles stay as the group's own
                    // pickup values, which is correct.
                  }
                  // sources.size > 1: mixed → keep pickup branch fallback.
                }

                // DEV-51046: after a move, a collection branch left behind
                // more than 10 miles out says so in place of its plain
                // distance. DEV-51278: the warning is also the way to fix
                // it — tapping it opens Manage Order on its branch list —
                // and it stays until the line changes branch or leaves the
                // basket, which is when it stops matching the record below.
                const keptSinceMove = group.items.some(
                  (item) =>
                    item.id != null &&
                    lineBranchesAtMove[item.id] === String(item.branchId),
                )
                const showFarBranchWarning = isFarCollectionBranch(
                  keptSinceMove,
                  groupFulfillment,
                  headerDistanceMiles,
                )

                return (
                <div
                  key={`${group.supplierId}-${group.branchId}-${group.fulfillmentMethod}-${group.isBackorder ? 'bo' : 'is'}`}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  {/* BM-013: Branch card header — fulfillment label + max
                      availability date across the group's items, branch name
                      + distance, item count. BM-014: Manage order button
                      opens the ManageOrderSheet.
                      DEV-51018: backorder groups keep the same neutral card
                      styling as in-stock siblings — the section header
                      above them says "Backorder", and each item's amber
                      pill (via CartItemStockRow) marks the individual
                      lines. Header label swaps the date for the SLA. */}
                  <div className="bg-gray-50 px-4 sm:px-5 py-3 border-b border-gray-100">
                    {/* Row 1: Fulfillment label + date | Manage order */}
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-sm font-[650] text-gray-950 uppercase">
                        {group.isBackorder ? (
                          <>
                            {groupFulfillment === 'pickup' ? 'Collection' : 'Delivery'}
                            {/* DEV-51018: backorder dates are estimates —
                                prefix with "Est." Show nothing when the
                                supplier didn't give a date; a wrong
                                fallback would mislead the engineer. */}
                            {groupMaxDateLabel && <> · Est. {groupMaxDateLabel}</>}
                          </>
                        ) : (
                          <>
                            {groupFulfillment === 'pickup' ? 'Ready to collect' : 'Delivered'}
                            {groupMaxDateLabel && <> · {groupMaxDateLabel}</>}
                          </>
                        )}
                      </span>
                      {group.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setManageOrderGroup(group)}
                          className="text-xs font-semibold text-gray-500 rounded px-1.5 py-0.5 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
                          aria-label={`Manage order for ${group.branchName}`}
                        >
                          Manage order
                        </button>
                      )}
                    </div>
                    {/* Row 2: Branch name + distance | Item count */}
                    <div
                      className={clsx(
                        'flex justify-between gap-3 text-xs',
                        showFarBranchWarning ? 'items-start' : 'items-center',
                      )}
                    >
                      <div
                        className={clsx(
                          'flex items-center gap-2 text-gray-600 min-w-0',
                          showFarBranchWarning && 'flex-wrap gap-y-1',
                        )}
                      >
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-gray-600 flex-shrink-0">
                          {headerBranchName}
                        </span>
                        {headerDistanceMiles != null && (
                          <>
                            {/* On phones the far-branch warning drops to its
                                own line, so this separator only shows while
                                it sits inline (sm and up). */}
                            <span
                              className={clsx(
                                'text-gray-500',
                                showFarBranchWarning && 'hidden sm:inline',
                              )}
                            >
                              ·
                            </span>
                            {showFarBranchWarning ? (
                              // Own line on phones, indented under the branch
                              // name (pin width + gap); inline from sm up.
                              <button
                                type="button"
                                onClick={() => {
                                  setManageOrderShowsBranches(true)
                                  setManageOrderGroup(group)
                                }}
                                className="min-w-0 basis-full pl-[1.375rem] text-left font-semibold text-amber-800 hover:underline sm:basis-auto sm:pl-0"
                              >
                                {headerDistanceMiles.toFixed(1)} miles away · Change branch
                              </button>
                            ) : (
                              <span>{headerDistanceMiles.toFixed(1)} mi away</span>
                            )}
                          </>
                        )}
                      </div>
                      <p className="text-xs font-semibold text-gray-700 flex-shrink-0">
                        {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {/* BM-014: per-branch pickup/delivery toggle removed; the
                      Manage Order sheet (opened from the header button)
                      now drives method + branch changes. */}

                  {/* Branch Inactive Warning */}
                  {isBranchInactive && (
                    <div className="bg-red-50 border-b border-red-200 px-4 sm:px-5 py-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                          <span className="text-red-600 text-xs font-bold">!</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-red-800 text-sm font-semibold mb-1">
                            Branch no longer available
                          </p>
                          <p className="text-red-700 text-xs">
                            This branch is currently inactive. Please remove these items or contact support for assistance.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cart Items */}
                  <div className="divide-y divide-gray-100">
                    {group.items.map((item) => {
                      const isAvailable = supportsSelectedFulfillment(item, groupFulfillment)
                      const availableStock = getAvailableStock(item, groupFulfillment)
                      const itemQuantity = item.id ? (pendingQuantities.get(item.id) ?? item.quantity) : item.quantity
                      const lineCap = item.capTotal ?? item.maxStock ?? availableStock
                      const lineCanBackorder = canBackorder(item, groupFulfillment)
                      // A qty above the channel's stock (or zero stock) is a
                      // backorder when allowed — no "Exceeds stock"; the group
                      // header rolls up the backorder date.
                      const isOverStock = itemQuantity > availableStock && !lineCanBackorder
                      const orderMultiple = item.quantityMultiple ?? 1
                      const isInvalidMultiple = orderMultiple > 1 && itemQuantity % orderMultiple !== 0
                      // item.price is the per-sell-unit price (e.g. per pack of 10);
                      // per-unit divides by the multiple.
                      const cartSellUom = item.unitOfMeasure || 'Each'
                      const cartUnitPrice = perUnitPrice(item)

                      const itemKey = `${item.productId}-${item.branchId}`
                      const hasLineStockCheckInFlight = item.id ? checkingStockItemIds.has(item.id) : false
                      const isLineCheckingStock = hasLineStockCheckInFlight && !isOverStock
                      const pickupOption = item.fulfillmentOptions?.find(option => option.type === 'pickup')
                      const deliveryOption = item.fulfillmentOptions?.find(option => option.type === 'delivery')
                      const canCollect = item.canCollect !== false
                      const canDeliver = item.canDeliver !== false
                      const branchStock = item.branchStock ?? (pickupOption?.stockQuantity ?? 0)
                      const dcStock = item.dcStock ?? 0
                      const pickupAvailableNow = branchStock
                      const pickupOverflowQty = Math.max(itemQuantity - branchStock, 0)
                      const branchPickupDateLabel = formatEarliestDate(item.branchEarliestCollection)
                      // Phase 2: ONLY the explicit pickup_dc_date. No
                      // fallback to branch's earliest_collection — that's a
                      // BRANCH date, not DC. When null, label falls back to
                      // generic "Later" downstream.
                      const oodPickupDateLabel = formatEarliestDate(item.pickupDcDate)
                      // LATER quantity = depot excess OVER branch (display-only
                      // subtraction; dc_stock in the schema stays raw). When
                      // dc==branch, this is 0 and the ``> 0`` gate hides the
                      // redundant row.
                      // Phase 3: adapter-supplied LATER qty. No fallback math.
                      const pickupLaterQty = item.pickupDcStock ?? 0
                      // LATER date: when pickup primary already has BOTH stock
                      // and date, LATER stays generic ("Later"). When primary
                      // is empty/incomplete, surface the genuine DC date.
                      const hasPickupPrimaryComplete = pickupAvailableNow > 0 && branchPickupDateLabel != null
                      // Phase 2: trust the adapter. pickupDcDate is set by
                      // the backend only when a real DC date applies. Frontend
                      // just reads it; null → generic "Later" label downstream.
                      const pickupLaterDateLabel =
                        formatEarliestDate(item.pickupDcDate) ?? oodPickupDateLabel ?? null
                      const hasPickupLaterDate = pickupLaterDateLabel != null
                      const pickupLaterLabel = pickupLaterDateLabel ?? '2-5 Business Days'
                      const pickupNowDisplayQty = Math.min(itemQuantity, pickupAvailableNow)
                      const pickupLaterDisplayQty = Math.min(pickupOverflowQty, pickupLaterQty)
                      const pickupNowSentenceDate = branchPickupDateLabel ?? 'Date not available'
                      const showPickupPrimaryQuantity = itemQuantity > pickupAvailableNow
                      // Hide the LATER row when its qty matches the primary
                      // tile's qty (avoids duplicate-looking row).
                      const showPickupLaterTier =
                        canCollect && item.dcStock != null && pickupLaterQty > 0 && pickupLaterQty !== pickupAvailableNow
                      const isCheckingPickupAvailability =
                        isLineCheckingStock &&
                        showPickupLaterTier &&
                        itemQuantity > branchStock &&
                        itemQuantity <= lineCap &&
                        pickupLaterDisplayQty > 0
                      // Phase 4: adapter now emits delivery_stock as branch-
                      // only deliverable. Frontend reads it directly.
                      const hasRealDeliveryData = !!item.branchEarliestDelivery
                      const deliveryPrimaryQty = deliveryOption?.stockQuantity ?? 0
                      const deliveryPrimaryLabel = formatEarliestDate(item.branchEarliestDelivery)
                      // Delivery LATER: subtract branch only when the primary
                      // actually showed it (hasRealDeliveryData). When primary
                      // was empty (branch couldn't deliver), LATER shows the
                      // FULL DC — the branch portion was never accounted for.
                      // Phase 3: adapter-supplied delivery LATER qty. No
                      // fallback math.
                      const deliveryLaterQty = item.deliveryDcStock ?? 0
                      const deliveryDateLabel = formatEarliestDate(item.oodEarliestDelivery ?? deliveryOption?.earliestDate)
                      // Delivery LATER date: show the real DC date when EITHER
                      // qty exceeds branch OR primary is incomplete (no stock
                      // / no date). Otherwise keep label generic ("Later").
                      // LATER tier prefers dcAvailableDate (the real depot
                      // arrival date); deliveryDateLabel is from the branch's
                      // earliest_delivery and used only as a fallback when
                      // the supplier didn't surface a separate DC date.
                      // Phase 2: trust the adapter. deliveryDcDate is set by
                      // the backend only when a real DC date applies. NO
                      // fallback to deliveryDateLabel — that's the branch's
                      // earliest_delivery, surfacing it as DC would mislabel
                      // a branch date as a DC date. When null, the label
                      // falls back to generic "Later" downstream.
                      const exceedsBranchStockDelivery = itemQuantity > branchStock
                      const hasDeliveryPrimaryComplete = deliveryPrimaryQty > 0 && deliveryPrimaryLabel != null
                      const deliveryLaterDateLabel = formatEarliestDate(item.deliveryDcDate)
                      const hasDeliveryLaterDate = deliveryLaterDateLabel != null
                      const deliveryLaterLabel = deliveryLaterDateLabel ?? '2-5 Business Days'
                      const deliveryCapacity = Math.max(deliveryPrimaryQty, deliveryLaterQty)
                      const deliveryDisplayQty = Math.min(itemQuantity, deliveryCapacity)
                      const deliveryDisplayDate =
                        itemQuantity > deliveryPrimaryQty
                          ? deliveryLaterLabel
                          : (deliveryPrimaryLabel ?? deliveryLaterLabel)
                      const deliverySentenceDate = deliveryDisplayDate
                      const stockSummaryBranchQty = Math.max(pickupAvailableNow, 0)
                      const stockSummaryWarehouseQty = item.dcStock == null ? null : Math.max(item.dcStock, 0)
                      // Tile-header quantities (UI only — no date logic).
                      // Mirrors SupplierListingCard.
                      const pickupHeaderQty = canCollect ? stockSummaryBranchQty : 0
                      const deliveryHeaderQty = deliveryPrimaryQty
                      // Same duplicate-row guard for delivery.
                      const showDeliveryLaterTier =
                        canDeliver && item.dcStock != null && deliveryLaterQty > 0 && deliveryLaterQty !== deliveryPrimaryQty
                      const isCheckingDeliveryAvailability =
                        isLineCheckingStock &&
                        canDeliver &&
                        item.dcStock != null &&
                        itemQuantity > branchStock &&
                        itemQuantity <= lineCap
                      // DEV-50924: per-item stock pill + earliest date.
                      // Resolver returns null when the state duplicates an
                      // existing warning row (stockUnavailable, channel
                      // closed, over-stock without backorder).
                      const stockView = getCartItemStockView(
                        item,
                        groupFulfillment,
                        isLineCheckingStock,
                        availableStock,
                        itemQuantity,
                      )
                      return (
                        <div
                          key={itemKey}
                          className={clsx(
                            'px-4 sm:px-5 py-3',
                            (!isAvailable || !!item.restrictionReason || !!item.stockUnavailable) && 'opacity-70 bg-gray-50'
                          )}
                        >
                          {/* BM-015: Mock-style compact item row.
                              Row 1: product name (link) + SKU/price + line total.
                              Small feature badges (preferred / restricted /
                              stock unavailable) sit under Row 1 so the tenant
                              features that don't exist in the mock still
                              surface — just compactly. */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <button
                                onClick={() =>
                                  navigate(
                                    `/${tenantSlug}/engineer/supplier-product/${item.productId}${item.branchId ? `?branchId=${item.branchId}` : ''}`
                                  )
                                }
                                className="text-left"
                              >
                                <p className="text-sm font-semibold text-gray-950 leading-snug hover:underline">
                                  {item.productName}
                                </p>
                              </button>
                              {(item.supplierProductCode || !item.priceUnavailable) && (
                                <p className="mt-1 text-xs text-gray-600">
                                  {item.supplierProductCode && <>{item.supplierProductCode}</>}
                                  {item.supplierProductCode && !item.priceUnavailable && ' · '}
                                  {!item.priceUnavailable && (
                                    <>£{item.price.toFixed(2)} / {orderMultiple > 1 ? `${cartSellUom} of ${orderMultiple}` : 'each'}</>
                                  )}
                                </p>
                              )}
                            </div>
                            <p className="text-sm font-bold text-gray-950 flex-shrink-0">
                              {item.priceUnavailable
                                ? <span className="text-gray-400">£-</span>
                                : `£${roundToPenny(cartUnitPrice * itemQuantity).toFixed(2)}`}
                            </p>
                          </div>

                          {stockView && <CartItemStockRow view={stockView} />}

                          {(item.isPreferred || item.isRestricted || item.stockUnavailable) && (
                            <div className="mt-2 flex items-center flex-wrap gap-1.5">
                              {item.isPreferred && (
                                <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">PREFERRED</span>
                              )}
                              {item.isRestricted && (
                                <span className="text-[10px] bg-red-50 text-red-700 px-1.5 py-0.5 rounded font-semibold">RESTRICTED</span>
                              )}
                              {item.stockUnavailable && (
                                <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-semibold">STOCK UNAVAILABLE</span>
                              )}
                            </div>
                          )}

                          {/* Van Stock toggle — tenant feature not in mock, kept compact. */}
                          {item.isVanStock && item.id && (() => {
                            const isToggled = item.isVanStockToggled ?? true
                            return (
                              <div className="mt-2">
                                <VanStockToggle
                                  enabled={isToggled}
                                  onChange={(val) => updateVanStockToggle(item.id!, val)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            )
                          })()}

                          {/* BM-015: Per-item stock tiles + Product info
                              accordion removed to match the mock. Live stock
                              is still enforced via the qty stepper cap and
                              the compact STOCK UNAVAILABLE / Exceeds-stock
                              messages below. SKU + unit price are shown
                              inline under the product name (Row 1). */}

                          {/* Price Change Indicator */}
                          {item.priceAtAdded && (
                            <div className="mb-3">
                              <PriceChangeBadge priceAtAdded={item.priceAtAdded} currentPrice={item.price} />
                            </div>
                          )}

                          {/* BM-015: Mock-style controls row.
                              Left: qty stepper (via QuantitySelector, which
                              already enforces the live stock cap) + small
                              delete icon. Right: Change item link.
                              Subtotal is already in Row 1 as the line total. */}
                          <div className="mt-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col gap-1">
                                <QuantitySelector
                                  size="sm"
                                  value={itemQuantity}
                                  onChange={(qty) => {
                                    if (!item.id) return
                                    if (qty === null) {
                                      setEmptyQtyItemIds(prev => new Set(prev).add(item.id!))
                                    } else {
                                      setEmptyQtyItemIds(prev => { const s = new Set(prev); s.delete(item.id!); return s })
                                      handleUpdateQuantity(item.id, qty, availableStock)
                                    }
                                  }}
                                  multiple={orderMultiple}
                                  stock={lineCanBackorder ? undefined : availableStock}
                                  warning={isInvalidMultiple}
                                  disabled={!isAvailable}
                                  ariaDescribedBy={isInvalidMultiple ? `cart-multiple-warn-${itemKey}` : undefined}
                                />
                                {isInvalidMultiple && (
                                  <span
                                    id={`cart-multiple-warn-${itemKey}`}
                                    className="text-xs text-amber-700"
                                  >
                                    ⚠ Sold in multiples of {orderMultiple}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() =>
                                  item.id && setItemToRemove({
                                    itemId: item.id,
                                    productName: item.productName,
                                  })
                                }
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Remove item"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            {/* BM-015: Change item opens the Change Item sheet
                                (supplier / branch / method / qty picker). */}
                            <button
                              type="button"
                              onClick={() => setChangeItem(item)}
                              className="text-xs font-semibold text-gray-500 rounded px-1.5 py-0.5 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              Modify
                            </button>
                          </div>

                          {/* Inline warnings. All optional. */}
                          {(item.restrictionLabel || !isAvailable || isOverStock) && (
                            <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                              <div className="flex-1 min-w-0">
                                {item.restrictionLabel ? (
                                  <span className="text-amber-600">{item.restrictionLabel}</span>
                                ) : !isAvailable ? (
                                  <span className="text-amber-600">Not available for {groupFulfillment === 'pickup' ? 'collection' : 'delivery'}</span>
                                ) : isOverStock ? (
                                  <span className="text-red-600">⚠️ Exceeds stock ({availableStock} available)</span>
                                ) : null}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* BM-015: Mock-style branch footer — total on the left,
                      Checkout button on the right. "Price unavailable"
                      surfaces above only when it applies, keeping the row
                      clean in the common case. */}
                  <div className="border-t border-gray-100 bg-gray-50 px-4 sm:px-5 py-3">
                    {hasBranchUnavailablePrice && (
                      <div className="mb-2">
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                          Price unavailable on {group.items.filter(i => i.priceUnavailable).length} item{group.items.filter(i => i.priceUnavailable).length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-lg font-bold text-gray-950">
                        {hasBranchUnavailablePrice ? <span className="text-gray-400">£-</span> : `£${branchTotal.toFixed(2)}`}
                      </p>
                      <Button
                        onClick={() => checkoutBranch(group)}
                        size="sm"
                        disabled={!hasAvailableItems || isBranchInactive || hasOverStockItems || hasEmptyQty || hasRestrictedItems || hasInvalidMultipleItems}
                        className="flex-shrink-0"
                      >
                        Checkout →
                      </Button>
                    </div>
                  </div>
                </div>
              )})}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                )
              })}
            </div>
          </>
        )}
      </main>

      {/* Sticky Footer - Cart Summary (Round 1: No Checkout All, checkout per branch only) */}
      {cart.length > 0 && (
        <div ref={toastClearanceRef} className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-gray-900 font-bold text-lg whitespace-nowrap">
                    {hasUnavailablePrice ? <span className="text-gray-400">£-</span> : `£${grandTotal.toFixed(2)}`}
                  </span>
                  <span className="text-gray-500 text-sm whitespace-nowrap">
                    <span className="font-medium text-gray-600">{totalItems}</span> item{totalItems !== 1 ? 's' : ''} across {branchGroups.length} branch{branchGroups.length !== 1 ? 'es' : ''}
                  </span>
                </div>
                {!hasUnavailablePrice && (
                  <p className="text-gray-400 text-xs mt-0.5">
                    Checkout each branch separately above
                  </p>
                )}
              </div>
              {/* TODO: Uncomment for Checkout All feature
              <Button
                onClick={checkoutAll}
                size="md"
                className="flex-shrink-0"
                disabled={totalItems === 0 || hasInactiveBranch}
              >
                Checkout All →
              </Button>
              */}
            </div>
          </div>
        </div>
      )}

      {/* BM-014: Manage Order sheet — mounted only while a group is set
          so its data-fetching effect doesn't run for a stale group. */}
      {manageOrderGroup && (
        <ManageOrderSheet
          open={true}
          branchGroup={manageOrderGroup}
          currentMethod={getGroupFulfillment(manageOrderGroup)}
          postcode={session?.postcode}
          latitude={session?.latitude}
          longitude={session?.longitude}
          showBranchListOnOpen={manageOrderShowsBranches}
          onClose={() => {
            setManageOrderGroup(null)
            setManageOrderShowsBranches(false)
          }}
          onApply={applyManageOrder}
        />
      )}

      {/* BM-015: Change Item sheet — same mount-when-open pattern.
          Seeds initialQuantity with pendingQuantities when the engineer
          typed an over-cap qty on the card that hasn't been synced to
          the cache — otherwise the sheet would open at the last
          committed item.quantity and drop the visible qty back down. */}
      {changeItem && (
        <ChangeItemSheet
          open={true}
          item={changeItem}
          currentMethod={getItemFulfillment(changeItem)}
          postcode={session?.postcode}
          latitude={session?.latitude}
          longitude={session?.longitude}
          initialQuantity={
            changeItem.id ? pendingQuantities.get(changeItem.id) : undefined
          }
          onClose={() => setChangeItem(null)}
          onApply={applyChangeItem}
        />
      )}

      {/* Remove Item Confirmation Dialog */}
      <Dialog.Root open={!!itemToRemove} onOpenChange={(open) => !open && setItemToRemove(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-2xl shadow-xl p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <Dialog.Title className="text-gray-900 font-bold text-lg mb-2">
                    Remove item?
                  </Dialog.Title>
                  <Dialog.Description className="text-gray-600 text-sm">
                    Are you sure you want to remove <strong>{itemToRemove?.productName}</strong> from your basket?
                  </Dialog.Description>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setItemToRemove(null)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemoveItem}
                  disabled={isRemoving}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:bg-red-300 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {isRemoving ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </>
                  )}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Clear Cart Confirmation Dialog */}
      <Dialog.Root open={showClearConfirm} onOpenChange={(open) => !open && setShowClearConfirm(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-2xl shadow-xl p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <Dialog.Title className="text-gray-900 font-bold text-lg mb-2">
                    Clear basket?
                  </Dialog.Title>
                  <Dialog.Description className="text-gray-600 text-sm">
                    This removes all {cart.length} item{cart.length !== 1 ? 's' : ''} from your basket. This can't be undone.
                  </Dialog.Description>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmClearCart}
                  disabled={isClearingCart}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:bg-red-300 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {isClearingCart ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Clearing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Clear basket
                    </>
                  )}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Profile Incomplete Dialog */}
      <Dialog.Root open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-2xl shadow-xl p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <UserRound className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <Dialog.Title className="text-gray-900 font-bold text-lg mb-1">
                    Complete your profile
                  </Dialog.Title>
                  <Dialog.Description className="text-gray-600 text-sm">
                    Please complete your profile before placing an order.
                  </Dialog.Description>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowProfileDialog(false)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => navigate(`/${tenantSlug}/engineer/more/profile?returnTo=cart`)}
                  className="flex-1 px-4 py-3 bg-brand-primary hover:bg-brand-primary-hover active:bg-brand-primary-dark text-[var(--brand-primary-text)] font-medium rounded-xl transition-colors"
                >
                  Go to Profile
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </EngineerLayout>
  )
}
